/**
 * OpportunityDetail component
 * 
 * This component displays detailed opportunity information from Salesforce,
 * including support for multiple contacts. The component has been enhanced
 * to support a secondary contact with the following features:
 * 
 * 1. Auto-detection of multiple contacts associated with an opportunity/account
 * 2. Ability to add or remove a secondary contact
 * 3. All contact fields are duplicated for the secondary contact with "Contact2" prefix
 * 4. Validation is applied to all secondary contact fields
 * 5. The UI visually distinguishes between primary and secondary contacts
 * 
 * The secondary contact data is integrated with the primary opportunity data
 * when syncing back to Salesforce.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Nav, Tab, Modal } from 'react-bootstrap';
import { getMappedOpportunityData, updateOpportunityWithFormData, getOpportunityFiles, getIndustryPicklistValues } from '../api/salesforceApi';
import { getOpportunityFields } from '../api/fieldMappings';
import { getActiveLenderSettings } from '../api/lenderApi';
import { checkUserLenderAccess } from '../api/userSettingsApi';
import { getAdvancedCriteriaSettings } from '../api/advancedCriteriaApi';
import { getBankStatementData } from '../api/bankDataMappings';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { LenderSubmission, FileUploads } from '../components/forms';
import SideNavigation from '../components/SideNavigation';
import { ValidatedInput } from '../components/forms';
import Select from 'react-select';
import { 
  validateSSN, 
  validatePhone, 
  validateEmail, 
  validateCurrency, 
  isValidRequestedAmount,
  validateZip, 
  validateEIN,
  validateDateOfBirth,
  validatePercentage,
  validateStreet,
  validateState,
  validateEntityType
} from '../utils';
import '../styles/OpportunityDetail.css';
import { FaArrowLeft, FaCheck, FaExclamationTriangle, FaSync, FaUserPlus, FaTimesCircle } from 'react-icons/fa';
import ValidationSummary from '../components/ValidationSummary';
import axios from 'axios';

// Debug: Check if the function is imported correctly
console.log('OpportunityDetail: getIndustryPicklistValues imported as:', getIndustryPicklistValues);

// List of US state codes in alphabetical order
const usStateCodes = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'AS', 'GU', 'MP', 'PR', 'VI'
];

// List of Canadian province/territory codes
const canadianProvinceCodes = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'
];

// Combined list of US states and Canadian provinces
const stateCodes = [...usStateCodes, ...canadianProvinceCodes];

// List of country codes (matching Salesforce format)
const countries = ['US', 'CA'];

const normalizeCountryCode = (value, defaultValue = '') => {
  if (value === null || value === undefined || value === '') return defaultValue;
  if (typeof value !== 'string') return value;

  const normalizedValue = value.trim().toLowerCase();
  if (!normalizedValue) return defaultValue;

  const countryMap = {
    'united states': 'US',
    'united states of america': 'US',
    'usa': 'US',
    'u.s.': 'US',
    'u.s.a.': 'US',
    'us': 'US',
    'canada': 'CA',
    'ca': 'CA'
  };

  return countryMap[normalizedValue] || value;
};

const formatToTwoDecimals = (value) => {
  if (value === null || value === undefined || value === '') return value;

  const numberValue = parseFloat(value);
  if (isNaN(numberValue)) return value;

  return numberValue.toFixed(2);
};

// List of entity types
const entityTypes = [
  'LLC', 'Partnership', 'Sole Proprietor', 'Corporation'
];

// Define a validator for Credit Score
const validateCreditScore = {
  validate: (value) => {
    const score = parseInt(value, 10);
    if (isNaN(score)) {
      return {
        isValid: false,
        message: 'Credit score must be a number'
      };
    }
    
    if (score < 250 || score > 900) {
      return {
        isValid: false,
        message: 'Credit score must be between 250 and 900'
      };
    }
    
    return {
      isValid: true,
      message: ''
    };
  }
};

// Custom styles for required asterisk
const requiredStyle = {
  color: 'var(--danger)',
  marginLeft: '2px'
};

// Custom styling for required field indicator
const requiredIndicatorStyle = {
  color: 'red',
  marginLeft: '2px'
};

// Determine which validation to use based on field name
const getValidatorForField = (field) => {
  const fieldName = field.name.toLowerCase();
  const fieldLabel = field.label.toLowerCase();
  
  if (fieldName.includes('ssn') || fieldName.includes('social') || fieldLabel.includes('social security')) {
    return validateSSN;
  } else if (fieldName.includes('phone') || fieldName.includes('mobile') || fieldName.includes('cell')) {
    return validatePhone;
  } else if (fieldName.includes('email')) {
    return validateEmail;
  } else if (fieldName.includes('zip') || fieldName.includes('postal')) {
    return validateZip;
  } else if (fieldName.includes('ein') || fieldName.includes('tax_id') || fieldLabel.includes('tax id')) {
    return validateEIN;
  } else if (fieldName.includes('dateofbirth') || fieldName.includes('birthdate') || fieldName.includes('dob')) {
    return validateDateOfBirth;
  } else if (fieldName.includes('percentage') || fieldName.includes('ownership')) {
    return validatePercentage;
  } else if (fieldName.includes('creditscore')) {
    return validateCreditScore;
  } else if (
    fieldName.includes('revenue') || 
    fieldName.includes('amount') || 
    fieldName.includes('balance') || 
    fieldName.includes('price') || 
    fieldName.includes('income') ||
    fieldName.includes('sales')
  ) {
    return validateCurrency;
  } else if (
    fieldName === 'businessstreet' || 
    fieldName === 'contactstreet' || 
    fieldName === 'contact2street' ||
    // Add more explicit detection for street fields
    (fieldName.includes('street') && 
     !fieldName.includes('city') && 
     !fieldName.includes('state') && 
     !fieldName.includes('zip'))
  ) {
    return validateStreet;
  } else if (
    fieldName === 'businessstate' || 
    fieldName === 'contactstate' || 
    fieldName === 'contact2state' ||
    fieldName.toLowerCase().includes('state')
  ) {
    return validateState;
  } else if (
    fieldName === 'entitytype' ||
    fieldName.toLowerCase().includes('entitytype')
  ) {
    return validateEntityType;
  }
  
  return null;
};

const OpportunityDetail = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Add state for the active financing tab
  const [activeFinancingTab, setActiveFinancingTab] = useState('working-capital');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [allContacts, setAllContacts] = useState([]); // Store all contacts retrieved from Salesforce
  const [hasSecondaryContact, setHasSecondaryContact] = useState(false); // Flag to show/hide secondary contact
  const [businessData, setBusinessData] = useState({});
  const [businessFields, setBusinessFields] = useState([]);
  const [categories, setCategories] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [canSubmitToLenders, setCanSubmitToLenders] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  
  // New state for the multi-step form
  const [activeSection, setActiveSection] = useState(0);
  const [completionStats, setCompletionStats] = useState({});
  
  // Add state for file uploads
  const [bankFiles, setBankFiles] = useState([]);
  const [appFiles, setAppFiles] = useState([]);
  const [quoteFiles, setQuoteFiles] = useState([]);  // Added for equipment financing
  const [creditFiles, setCreditFiles] = useState([]); // Added for equipment financing
  const [assetFormFiles, setAssetFormFiles] = useState([]); // Added for real estate
  
  // State for OptOut modal
  const [showOptOutModal, setShowOptOutModal] = useState(false);
  const [optOutUrl, setOptOutUrl] = useState('');
  const [fileUploadsComplete, setFileUploadsComplete] = useState(false);
  const [fileUploadStats, setFileUploadStats] = useState({ completed: 0, total: 2 });
  
  const { success, error: notifyError, warning } = useNotifications();
  
  // State for dynamic secondary contact fields
  const [secondaryContactFields, setSecondaryContactFields] = useState([]);
  const [secondaryFieldsLoading, setSecondaryFieldsLoading] = useState(true);
  
  // Add new state variables below the existing state variables
  const [salesforceFiles, setSalesforceFiles] = useState([]);
  const [loadingSfFiles, setLoadingSfFiles] = useState(false);
  const [sfFilesError, setSfFilesError] = useState(null);
  
  // State for dynamic industry picklist values
  const [industryOptions, setIndustryOptions] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(true);
  
  // State for preloaded lenders to eliminate loading delay when switching to submit tab
  const [workingCapitalLenders, setWorkingCapitalLenders] = useState([]);
  const [equipmentFinancingLenders, setEquipmentFinancingLenders] = useState([]);
  const [sbaLenders, setSbaLenders] = useState([]);
  const [realEstateLenders, setRealEstateLenders] = useState([]);
  const [lendersLoading, setLendersLoading] = useState(true);
  
  // State for preloaded advanced criteria settings (to speed up auto-decline display)
  const [advancedCriteriaSettings, setAdvancedCriteriaSettings] = useState(null);
  const [advancedCriteriaLoading, setAdvancedCriteriaLoading] = useState(true);
  
  // Fetch industry picklist values from Salesforce on component mount
  useEffect(() => {
    console.log('OpportunityDetail: useEffect for industries is running!');
    
    const fetchIndustries = async () => {
      try {
        console.log('OpportunityDetail: Starting to fetch industries...');
        console.log('OpportunityDetail: getIndustryPicklistValues function:', getIndustryPicklistValues);
        setLoadingIndustries(true);
        
        const industries = await getIndustryPicklistValues();
        console.log('OpportunityDetail: Received industries:', industries);
        console.log('OpportunityDetail: Type of industries:', typeof industries);
        console.log('OpportunityDetail: Is array?', Array.isArray(industries));
        
        if (Array.isArray(industries)) {
          const formattedOptions = industries.map(industry => ({ value: industry, label: industry }));
          console.log('OpportunityDetail: Formatted options:', formattedOptions);
          setIndustryOptions(formattedOptions);
        } else {
          console.error('OpportunityDetail: Industries is not an array:', industries);
          setIndustryOptions([]);
        }
      } catch (error) {
        console.error('OpportunityDetail: Error fetching industry picklist values:', error);
        console.error('OpportunityDetail: Error stack:', error.stack);
        // Fallback to empty array if fetch fails
        setIndustryOptions([]);
      } finally {
        setLoadingIndustries(false);
        console.log('OpportunityDetail: Finished loading industries, loadingIndustries set to false');
      }
    };
    
    fetchIndustries();
  }, []);
  
  // Fetch secondary contact field mappings when hasSecondaryContact changes
  useEffect(() => {
    if (!hasSecondaryContact) return;
    
    const fetchSecondaryContactFields = async () => {
      try {
        setSecondaryFieldsLoading(true);
        const response = await axios.get('/api/field-mappings');
        const mappings = response.data;
        
        // Get all secondary contact fields from mappings
        const fields = mappings.filter(mapping => 
          mapping.opportunityField.startsWith('Contact2') && 
          mapping.salesforceObject === 'Contact'
        );
        
        // If no mappings found, use default fields
        if (fields.length === 0) {
          console.warn('No secondary contact field mappings found. Using default fields.');
          // Use default fields similar to those in renderField function
          const defaultFields = [
            { opportunityField: 'Contact2FirstName', label: 'First Name' },
            { opportunityField: 'Contact2LastName', label: 'Last Name' },
            { opportunityField: 'Contact2Title', label: 'Title' },
            { opportunityField: 'Contact2SSN', label: 'SSN' },
            { opportunityField: 'Contact2DateOfBirth', label: 'Date of Birth' },
            { opportunityField: 'Contact2Email', label: 'Email' },
            { opportunityField: 'Contact2CellPhone', label: 'Cell Phone' },
            { opportunityField: 'Contact2OwnershipPercentage', label: 'Ownership Percentage' },
            { opportunityField: 'Contact2CreditScore', label: 'Credit Score' },
            { opportunityField: 'Contact2Street', label: 'Street' },
            { opportunityField: 'Contact2City', label: 'City' },
            { opportunityField: 'Contact2State', label: 'State' },
            { opportunityField: 'Contact2ZipCode', label: 'Zip/Postal Code' },
            { opportunityField: 'Contact2Country', label: 'Country' }
          ];
          setSecondaryContactFields(defaultFields);
        } else {
          // Transform the mappings into a usable format for rendering
          const formattedFields = fields.map(field => ({
            opportunityField: field.opportunityField,
            label: field.opportunityField.replace('Contact2', '')
          }));
          setSecondaryContactFields(formattedFields);
        }
      } catch (error) {
        console.error('Error fetching secondary contact field mappings:', error);
        // Use default fields on error
        const fallbackFields = [
          { opportunityField: 'Contact2FirstName', label: 'First Name' },
          { opportunityField: 'Contact2LastName', label: 'Last Name' },
          { opportunityField: 'Contact2Title', label: 'Title' },
          { opportunityField: 'Contact2SSN', label: 'SSN' },
          { opportunityField: 'Contact2DateOfBirth', label: 'Date of Birth' },
          { opportunityField: 'Contact2Email', label: 'Email' },
          { opportunityField: 'Contact2CellPhone', label: 'Cell Phone' },
          { opportunityField: 'Contact2OwnershipPercentage', label: 'Ownership Percentage' },
          { opportunityField: 'Contact2CreditScore', label: 'Credit Score' },
          { opportunityField: 'Contact2Street', label: 'Street' },
          { opportunityField: 'Contact2City', label: 'City' },
          { opportunityField: 'Contact2State', label: 'State' },
          { opportunityField: 'Contact2ZipCode', label: 'Zip/Postal Code' },
          { opportunityField: 'Contact2Country', label: 'Country' }
        ];
        setSecondaryContactFields(fallbackFields);
      } finally {
        setSecondaryFieldsLoading(false);
      }
    };
    
    fetchSecondaryContactFields();
  }, [hasSecondaryContact]);
  
  // Function to load and filter lenders for both financing types
  const loadAllLenders = async () => {
    try {
      console.log('Preloading lenders for all financing types...');
      
      // Fetch active lenders for all financing types in parallel
      const [workingCapitalLendersRaw, equipmentLendersRaw, sbaLendersRaw, realEstateLendersRaw] = await Promise.all([
        getActiveLenderSettings('working-capital'),
        getActiveLenderSettings('equipment-financing'),
        getActiveLenderSettings('sba'),
        getActiveLenderSettings('real-estate')
      ]);
      
      // Process working capital lenders
      const processedWCLenders = await processLendersWithAccess(workingCapitalLendersRaw, 'working-capital');
      setWorkingCapitalLenders(processedWCLenders);
      
      // Process equipment financing lenders
      const processedEFLenders = await processLendersWithAccess(equipmentLendersRaw, 'equipment-financing');
      setEquipmentFinancingLenders(processedEFLenders);
      
      // Process SBA lenders
      const processedSBALenders = await processLendersWithAccess(sbaLendersRaw, 'sba');
      setSbaLenders(processedSBALenders);
      
      // Process Real Estate lenders
      const processedRELenders = await processLendersWithAccess(realEstateLendersRaw, 'real-estate');
      setRealEstateLenders(processedRELenders);
      
      console.log(`Preloaded ${processedWCLenders.length} working capital, ${processedEFLenders.length} equipment financing, ${processedSBALenders.length} SBA, and ${processedRELenders.length} real estate lenders`);
    } catch (error) {
      console.error('Error preloading lenders:', error);
      // Set empty arrays on error so UI doesn't break
      setWorkingCapitalLenders([]);
      setEquipmentFinancingLenders([]);
      setSbaLenders([]);
      setRealEstateLenders([]);
    } finally {
      setLendersLoading(false);
    }
  };
  
  // Function to preload advanced criteria settings
  const loadAdvancedCriteriaSettings = async () => {
    try {
      console.log('Preloading advanced criteria settings...');
      const settings = await getAdvancedCriteriaSettings();
      console.log('📋 Advanced criteria settings preloaded:', settings);
      setAdvancedCriteriaSettings(settings);
    } catch (error) {
      console.error('Error preloading advanced criteria settings:', error);
      setAdvancedCriteriaSettings({ enabled: false, lenders: [] });
    } finally {
      setAdvancedCriteriaLoading(false);
    }
  };
  
  // Function to process lenders with user access checks
  const processLendersWithAccess = async (activeLenders, financingType) => {
    // Convert database format to component format
    // Include allowAutoDeclineOverride and requireConfirmation to avoid separate API calls later
    let formattedLenders = activeLenders.map(lender => ({
      id: lender.stringId,
      name: lender.lenderName,
      tier: lender.tier,
      emailOnly: lender.emailOnly,
      financingType: lender.financingType,
      allowAutoDeclineOverride: lender.allowAutoDeclineOverride !== false, // Default to true
      requireConfirmation: lender.requireConfirmation || false
    }));
    
    // Filter lenders based on user access (if user context is available)
    if (user && user.email) {
      console.log(`Checking lender access for user: ${user.email} (${financingType})`);
      
      // Check access for each lender
      const accessChecks = await Promise.allSettled(
        formattedLenders.map(async (lender) => {
          try {
            const accessResponse = await checkUserLenderAccess(user.email, lender.id);
            return {
              lenderId: lender.id,
              hasAccess: accessResponse.hasAccess,
              reason: accessResponse.reason
            };
          } catch (error) {
            console.warn(`Could not check access for lender ${lender.id}, defaulting to allow:`, error);
            return {
              lenderId: lender.id,
              hasAccess: true,
              reason: 'Access check failed, defaulted to allow'
            };
          }
        })
      );
      
      // Filter out lenders the user doesn't have access to
      const accessMap = {};
      accessChecks.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const { lenderId, hasAccess, reason } = result.value;
          accessMap[lenderId] = { hasAccess, reason };
          
          if (!hasAccess) {
            console.log(`User ${user.email} does not have access to ${lenderId}: ${reason}`);
          }
        }
      });
      
      // Filter lenders based on access
      const originalCount = formattedLenders.length;
      formattedLenders = formattedLenders.filter(lender => {
        const access = accessMap[lender.id];
        return access ? access.hasAccess : true; // Default to allow if check failed
      });
      
      const filteredCount = formattedLenders.length;
      console.log(`Filtered lenders for ${financingType}: ${originalCount} -> ${filteredCount} based on user access`);
    }
    
    // Sort by tier (ascending - tier 1 first)
    formattedLenders.sort((a, b) => a.tier - b.tier);
    
    return formattedLenders;
  };
  
  // Fetch contact and business data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setLoadingSfFiles(true);
        setSfFilesError(null);
        
        // Fetch business fields
        const fields = await getOpportunityFields();
        setBusinessFields(fields);
        console.log('Opportunity fields loaded:', fields.length);
        console.log('All business fields:', fields.map(f => ({ name: f.name, label: f.label, category: f.category })));
        
        // Check specifically for state and entity type fields
        const stateFields = fields.filter(f => f.name.includes('State') || f.name === 'EntityType');
        console.log('State and Entity fields found in businessFields:', stateFields);
        
        // Extract unique categories and apply our mapping to combine related categories
        // Filter out null categories (like Bank Statement Aggregates which are virtual/hidden)
        const mappedCategories = fields.map(field => getMappedCategory(field.category)).filter(cat => cat !== null);
        const uniqueCategories = [...new Set(mappedCategories)];
        setCategories(uniqueCategories);
        console.log('Categories:', uniqueCategories);
        
        // Fetch mapped opportunity data first (critical path)
        const opportunityData = await getMappedOpportunityData(opportunityId);

        // Fetch Salesforce file metadata separately so file delays do not block page load
        let filesData = [];
        try {
          filesData = await getOpportunityFiles(opportunityId);
          setSfFilesError(null);
        } catch (fileError) {
          console.error(`Error fetching Salesforce files for opportunity ${opportunityId}:`, fileError);
          setSfFilesError('Opportunity loaded, but Salesforce files could not be loaded in time. Large attachments may cause delays.');
        }
        
        // Preload lenders and advanced criteria settings to eliminate delay when switching tabs
        setLendersLoading(true);
        setAdvancedCriteriaLoading(true);
        
        // Load lenders and advanced criteria in parallel
        await Promise.all([
          loadAllLenders(),
          loadAdvancedCriteriaSettings()
        ]);
        
        console.log('\n=== COMPONENT: RECEIVED DATA FROM API ===');
        console.log('Contact data:', opportunityData.contactData);
        console.log('All contacts:', opportunityData.contactsData || []);
        console.log('Opportunity data:', opportunityData.opportunityData);
        console.log(`Salesforce files: ${filesData.length} files loaded concurrently`);
        
        // Store the files data
        setSalesforceFiles(filesData);
        
        // Ensure SIC Code is properly set
        if (!opportunityData.opportunityData?.SICCode || opportunityData.opportunityData.SICCode === '') {
          console.log('SIC Code is empty in the response data, setting default "0000"');
          opportunityData.opportunityData.SICCode = "0000";
        }
        
        // Uppercase any state field values to match the dropdown options
        if (opportunityData.opportunityData) {
          // Handle state fields - Uppercase them for proper state dropdown matching
          ['BusinessState', 'ContactState', 'Contact2State'].forEach(stateField => {
            if (opportunityData.opportunityData[stateField]) {
              opportunityData.opportunityData[stateField] = 
                opportunityData.opportunityData[stateField].toUpperCase();
            }
          });

          ['BusinessCountry', 'ContactCountry', 'Contact2Country'].forEach(countryField => {
            opportunityData.opportunityData[countryField] = normalizeCountryCode(
              opportunityData.opportunityData[countryField],
              'US'
            );
          });
        }
        
        // Store all contacts
        if (opportunityData.contactsData && Array.isArray(opportunityData.contactsData)) {
          setAllContacts(opportunityData.contactsData);
          
          // If more than one contact is found, enable secondary contact display
          if (opportunityData.contactsData.length > 1) {
            setHasSecondaryContact(true);
            
            // Add secondary contact fields to businessData
            const secondaryContact = opportunityData.contactsData[1]; // Get the second contact
            if (secondaryContact) {
              console.log('Secondary contact found, mapping fields...');
              // Create a copy of opportunity data
              const updatedBusinessData = { ...opportunityData.opportunityData };
              
              // Map secondary contact fields to our Contact2 format fields
              // Need to wait for the mapping to complete before setting business data
              await mapSecondaryContactToBusinessData(secondaryContact, updatedBusinessData);
              
              console.log('Secondary contact mapping complete, updatedBusinessData:', updatedBusinessData);
              // Update business data with the combined primary and secondary contact data
              setBusinessData(processBusinessDataForWholeNumbers(updatedBusinessData));
            } else {
              setBusinessData(processBusinessDataForWholeNumbers(opportunityData.opportunityData));
            }
          } else {
            setBusinessData(processBusinessDataForWholeNumbers(opportunityData.opportunityData));
          }
        } else {
          setBusinessData(processBusinessDataForWholeNumbers(opportunityData.opportunityData));
        }
        
        // Debug specific fields
        console.log('\n=== COMPONENT: CHECKING SPECIFIC FIELDS ===');
        console.log('Federal Tax ID:', opportunityData.opportunityData?.FederalTaxID);
        console.log('Business Legal Name:', opportunityData.opportunityData?.BusinessLegalName);
        console.log('Entity Type:', opportunityData.opportunityData?.EntityType);
        
        // Check for empty critical fields that should always be populated from Salesforce
        const criticalFields = [
          { field: 'BusinessLegalName', label: 'Business Legal Name' },
          { field: 'FederalTaxID', label: 'Federal Tax ID' },
          { field: 'Industry', label: 'Industry' },
          { field: 'ContactFirstName', label: 'Contact First Name' },
          { field: 'ContactLastName', label: 'Contact Last Name' }
        ];
        
        const missingFields = criticalFields.filter(({ field }) => {
          const value = opportunityData.opportunityData?.[field];
          return value === undefined || value === null || value === '';
        });
        
        if (missingFields.length > 0) {
          console.warn('\n=== COMPONENT: CRITICAL FIELD MAPPING ISSUES DETECTED ===');
          console.warn('The following critical fields are empty, which may indicate field mapping issues:');
          missingFields.forEach(({ field, label }) => {
            console.warn(`- ${label} (${field}): Missing or empty`);
          });
          console.warn('Please check field mappings in the Field Mappings page.');
        }
        
        setContactData(opportunityData.contactData);
        
        // Fetch bank statement data from configured mappings
        // Keep missing bank values empty so criteria do not interpret them as verified zeros.
        const fieldsToDefaultToEmpty = [
          'Month1NSFs', 'Month2NSFs', 'Month3NSFs', 'Month4NSFs',
          'Month1NegativeDays', 'Month2NegativeDays', 'Month3NegativeDays', 'Month4NegativeDays'
        ];
        
        try {
          console.log('\n=== COMPONENT: LOADING BANK STATEMENT DATA ===');
          const bankDataResponse = await getBankStatementData(opportunityId);
          
          // Start with unknown values for NSFs and Negative Days
          const bankData = {};
          fieldsToDefaultToEmpty.forEach(field => {
            bankData[field] = null;
          });
          
          if (bankDataResponse.success && bankDataResponse.data) {
            console.log('Bank statement data loaded:', bankDataResponse.data);
            // Merge the actual Salesforce values, including verified zeros.
            Object.assign(bankData, bankDataResponse.data);
          } else {
            console.log('No bank statement data configured or available, using defaults');
          }
          
          // Preserve missing NSFs and Negative Days as unknown.
          fieldsToDefaultToEmpty.forEach(field => {
            if (bankData[field] === null || bankData[field] === undefined) {
              bankData[field] = null;
            }
          });
          
          console.log('Final bank data with defaults:', bankData);
          
          // Calculate Average Monthly Sales from bank statement deposits
          // Month 1-3 are always included, Month 4 only if non-zero
          const depositFields = ['Month1Deposits', 'Month2Deposits', 'Month3Deposits', 'Month4Deposits'];
          const hasDepositData = depositFields.some(field => {
            const value = bankData[field];
            return value !== null && value !== undefined && value !== '' && !isNaN(parseFloat(value));
          });

          let calculatedAverageMonthlySales = opportunityData.opportunityData?.AverageMonthlySales;

          if (hasDepositData) {
            const month1 = parseFloat(bankData.Month1Deposits) || 0;
            const month2 = parseFloat(bankData.Month2Deposits) || 0;
            const month3 = parseFloat(bankData.Month3Deposits) || 0;
            const month4 = parseFloat(bankData.Month4Deposits) || 0;
            
            // Always use months 1-3
            let totalDeposits = month1 + month2 + month3;
            let monthCount = 3;
            
            // Only include month 4 if it has a non-zero value
            if (month4 > 0) {
              totalDeposits += month4;
              monthCount = 4;
            }
            
            const rawAverage = monthCount > 0 ? totalDeposits / monthCount : 0;
            // Round to nearest cent (2 decimal places) and format with trailing zeros
            calculatedAverageMonthlySales = rawAverage.toFixed(2);
            
            console.log('Calculated Average Monthly Sales:', {
              month1, month2, month3, month4,
              totalDeposits,
              monthCount,
              calculatedAverageMonthlySales
            });
          } else {
            console.log('No monthly deposit data found, using mapped Average Monthly Sales:', calculatedAverageMonthlySales);
          }
          
          // Merge bank statement data into businessData and set calculated AverageMonthlySales
          setBusinessData(prevData => ({
            ...prevData,
            ...bankData,
            ...(calculatedAverageMonthlySales !== undefined && calculatedAverageMonthlySales !== null && calculatedAverageMonthlySales !== ''
              ? { AverageMonthlySales: formatToTwoDecimals(calculatedAverageMonthlySales) }
              : {})
          }));
        } catch (bankDataError) {
          console.warn('Error loading bank statement data (non-critical):', bankDataError.message);
          // A failed lookup does not establish zero NSFs or negative days.
          const defaultBankData = {};
          fieldsToDefaultToEmpty.forEach(field => {
            defaultBankData[field] = null;
          });
          setBusinessData(prevData => ({
            ...prevData,
            ...defaultBankData
          }));
        }
        
        console.log('\n=== COMPONENT: STATE AFTER SETTING DATA ===');
        console.log('businessData state will be set to:', businessData);
        
        // Add a delayed check to see what's in the state after render
        setTimeout(() => {
          console.log('\n=== COMPONENT: DELAYED STATE CHECK ===');
          console.log('Current businessData state:', businessData);
          console.log('Federal Tax ID in state:', businessData.FederalTaxID);
          console.log('Has secondary contact:', hasSecondaryContact);
        }, 500);
      } catch (error) {
        console.error('Error fetching opportunity data:', error);
        
        if (error.response && error.response.status === 404) {
          setError('Opportunity not found. Please check the opportunity ID and try again.');
        } else {
          setError('Failed to load opportunity data. Please try again later.');
        }
      } finally {
        setLoading(false);
        setLoadingSfFiles(false);
      }
    };
    
    if (opportunityId) {
      fetchData();
    } else {
      setError('Opportunity ID is required');
      setLoading(false);
    }
  }, [opportunityId]);
  
  // Track if OptOut tab has been opened to prevent duplicates
  const optOutOpenedRef = useRef(false);
  const lastOpportunityIdRef = useRef(null);

  // Auto-open OptOut tab when page loads (if no submissions yet)
  useEffect(() => {
    const openOptOutTab = async () => {
      // Reset ref if opportunity changed
      if (lastOpportunityIdRef.current !== opportunityId) {
        optOutOpenedRef.current = false;
        lastOpportunityIdRef.current = opportunityId;
      }
      
      // Prevent duplicate runs for same opportunity
      if (optOutOpenedRef.current) {
        return;
      }
      
      // Wait until data is loaded
      if (loading || !opportunityId || !contactData || !businessData) {
        return;
      }
      
      // Mark as opened immediately to prevent race conditions
      optOutOpenedRef.current = true;
      
      try {
        // Check if OptOut flow is enabled via feature flag
        const configResponse = await axios.get('/api/config/features');
        if (!configResponse.data.optOutFlowEnabled) {
          console.log('OptOut flow is disabled via feature flag');
          return;
        }
        
        // Check if we should show OptOut for this opportunity
        const shouldShowResponse = await axios.get(`/api/prefill/should-show/${opportunityId}`);
        
        if (!shouldShowResponse.data.shouldShow) {
          console.log('OptOut already shown for this opportunity, skipping');
          return;
        }
        
        // Debug: Log what data is available
        console.log('📋 Available data for OptOut:', {
          contactData: contactData ? Object.keys(contactData) : 'null',
          businessData: businessData ? Object.keys(businessData) : 'null',
          firstName: contactData?.FirstName || businessData?.ContactFirstName,
          lastName: contactData?.LastName || businessData?.ContactLastName,
          phone: businessData?.ContactCellPhone,
          address: businessData?.BusinessStreet || businessData?.ContactStreet,
          city: businessData?.BusinessCity || businessData?.ContactCity,
          state: businessData?.BusinessState || businessData?.ContactState,
          zip: businessData?.BusinessZipCode || businessData?.ContactZipCode
        });
        
        // Prepare payload with correct field names (removed SSN and DOB, added phone)
        const prefillPayload = {
          firstName: contactData?.FirstName || businessData?.ContactFirstName || '',
          lastName: contactData?.LastName || businessData?.ContactLastName || '',
          phone: businessData?.ContactCellPhone || '',
          address: businessData?.BusinessStreet || businessData?.ContactStreet || '',
          city: businessData?.BusinessCity || businessData?.ContactCity || '',
          state: businessData?.BusinessState || businessData?.ContactState || '',
          zipCode: businessData?.BusinessZipCode || businessData?.ContactZipCode || ''
        };
        
        // Check if we have the minimum required data
        if (!prefillPayload.firstName || !prefillPayload.lastName) {
          console.log('Missing required contact data for OptOut, skipping');
          return;
        }
        
        // Create token (this will also mark tab as opened in the backend)
        const tokenResponse = await axios.post('/api/prefill/create-token', {
          payload: prefillPayload,
          opportunityId
        });
        
        console.log('📤 Payload sent to API:', prefillPayload);
        
        if (tokenResponse.data.success) {
          const token = tokenResponse.data.token;
          const url = `https://www.optoutprescreen.com/#prefillToken=${token}`;
          
          // Set URL and show modal instead of auto-opening
          setOptOutUrl(url);
          setShowOptOutModal(true);
          
          console.log('OptOut modal displayed with prefill token');
        }
        
      } catch (error) {
        console.error('Error opening OptOut tab:', error);
        // Fail silently - this is not critical to the main workflow
      }
    };
    
    // Only run once when data is loaded
    openOptOutTab();
  }, [loading, opportunityId, contactData, businessData]);
  
  // Function to map secondary contact fields to businessData
  const mapSecondaryContactToBusinessData = (secondaryContact, businessData) => {
    if (!secondaryContact) return Promise.resolve();
    
    // Return a Promise that resolves when the mapping is complete
    return new Promise(async (resolve, reject) => {
      try {
        // Get all field mappings from the database
        let mappings = [];
        try {
          const response = await axios.get('/api/field-mappings');
          mappings = response.data;
          console.log('Retrieved field mappings for secondary contact:', mappings.length);
        } catch (error) {
          console.error('Error fetching field mappings:', error);
          // Continue with empty mappings
        }
        
        console.log('Mapping secondary contact fields using field mappings...');
        console.log('Secondary contact data:', secondaryContact);
        
        // Get primary contact field mappings (these map ContactX fields to Salesforce Contact fields)
        const primaryContactMappings = mappings.filter(mapping => 
          mapping.opportunityField.startsWith('Contact') && 
          !mapping.opportunityField.startsWith('Contact2') &&
          mapping.salesforceObject === 'Contact'
        );
        
        console.log('Found primary contact mappings:', primaryContactMappings.length);
        
        // For each primary contact mapping, create an equivalent secondary contact mapping
        primaryContactMappings.forEach(mapping => {
          // Get primary field name (e.g., ContactFirstName)
          const primaryField = mapping.opportunityField;
          
          // Create secondary field name (e.g., Contact2FirstName)
          const secondaryField = primaryField.replace('Contact', 'Contact2');
          
          // Get the Salesforce field from the primary mapping (e.g., FirstName)
          const salesforceField = mapping.salesforceField;
          
          // Map the secondary contact's Salesforce field to the secondary field in businessData
          if (primaryField === 'ContactCreditScore' && 
              (!secondaryContact[salesforceField] || secondaryContact[salesforceField] === '')) {
            // For Credit Score, if the value doesn't exist, set to empty string
            businessData[secondaryField] = '';
          } else {
            // Get the value and handle state fields specially
            let value = secondaryContact[salesforceField] || '';
            
            // Uppercase state values to match with dropdown options
            if (secondaryField === 'Contact2State' && value) {
              value = value.toUpperCase();
            }

            if (secondaryField === 'Contact2Country') {
              value = normalizeCountryCode(value, 'US');
            }
            
            businessData[secondaryField] = value;
          }
          
          console.log(`Mapped ${salesforceField} from secondary contact to ${secondaryField}: "${businessData[secondaryField]}"`);
        });
        
        // Ensure Credit Score is included even if it's not in the mappings
        if (!primaryContactMappings.some(mapping => mapping.opportunityField === 'ContactCreditScore')) {
          // Default Credit Score field names in Salesforce
          const creditScoreFields = ['Credit_Score__c', 'CreditScore__c'];
          
          // Check if any of these fields exist in the secondary contact
          let foundCreditScore = false;
          for (const fieldName of creditScoreFields) {
            if (secondaryContact[fieldName] !== undefined) {
              businessData['Contact2CreditScore'] = secondaryContact[fieldName] || '';
              console.log(`Mapped ${fieldName} from secondary contact to Contact2CreditScore: "${businessData['Contact2CreditScore']}"`);
              foundCreditScore = true;
              break;
            }
          }
          
          // If no Credit Score field was found, initialize with empty string
          if (!foundCreditScore) {
            businessData['Contact2CreditScore'] = '';
            console.log('Initialized Contact2CreditScore to empty string as no Credit Score field was found in the secondary contact');
          }
        }
        
        console.log('Secondary contact mapped to business data:', businessData);
        resolve();
      } catch (error) {
        console.error('Error mapping secondary contact:', error);
        // Resolve anyway to prevent blocking
        resolve();
      }
    });
  };
  
  // Function to add a second contact when none exists
  const addSecondaryContact = () => {
    setHasSecondaryContact(true);
    
    // Get field mappings from the database
    const getFieldMappings = async () => {
      try {
        const response = await axios.get('/api/field-mappings');
        return response.data;
      } catch (error) {
        console.error('Error fetching field mappings:', error);
        return [];
      }
    };
    
    // Initialize a new secondary contact with empty fields
    const initializeSecondaryContact = async () => {
      // Add empty secondary contact fields to businessData
      const updatedBusinessData = { ...businessData };
      
      try {
        // Get field mappings from database
        const mappings = await getFieldMappings();
        
        // Get all secondary contact fields from mappings
        const secondaryContactFields = mappings.filter(mapping => 
          mapping.opportunityField.startsWith('Contact2') && 
          mapping.salesforceObject === 'Contact'
        );
        
        // If no secondary contact mappings are found, use a default set
        if (secondaryContactFields.length === 0) {
          console.warn('No secondary contact field mappings found in database. Using default fields.');
          // Default fields if no mappings exist
          const defaultFields = [
            'Contact2FirstName', 'Contact2LastName', 'Contact2Title', 
            'Contact2Email', 'Contact2CellPhone', 'Contact2SSN', 
            'Contact2DateOfBirth', 'Contact2OwnershipPercentage', 'Contact2CreditScore',
            'Contact2Street', 'Contact2City', 'Contact2State', 'Contact2ZipCode'
          ];
          
          // Initialize default fields with empty values
          defaultFields.forEach(field => {
            updatedBusinessData[field] = '';
          });
        } else {
          // Initialize mapped fields with empty values
          secondaryContactFields.forEach(mapping => {
            updatedBusinessData[mapping.opportunityField] = '';
          });
        }
        
        // Update business data with empty secondary contact fields
        setBusinessData(updatedBusinessData);
        
      } catch (error) {
        console.error('Error initializing secondary contact fields:', error);
        // Fallback to basic fields if there's an error
        const basicFields = [
          'Contact2FirstName', 'Contact2LastName', 'Contact2Title', 
          'Contact2Email', 'Contact2CellPhone', 'Contact2SSN', 
          'Contact2DateOfBirth', 'Contact2OwnershipPercentage', 'Contact2CreditScore',
          'Contact2Street', 'Contact2City', 'Contact2State', 'Contact2ZipCode'
        ];
        
        basicFields.forEach(field => {
          updatedBusinessData[field] = '';
        });
        
        setBusinessData(updatedBusinessData);
      }
    };
    
    // Execute the initialization
    initializeSecondaryContact();
  };
  
  // Function to remove the secondary contact
  const removeSecondaryContact = () => {
    setHasSecondaryContact(false);
    
    // Create a copy of businessData
    const updatedBusinessData = { ...businessData };
    
    // Remove all Contact2 fields
    Object.keys(updatedBusinessData).forEach(key => {
      if (key.startsWith('Contact2')) {
        delete updatedBusinessData[key];
      }
    });
    
    setBusinessData(updatedBusinessData);
    
    // Remove any validation errors for the secondary contact fields
    const filteredErrors = validationErrors.filter(
      error => !error.field.startsWith('Contact2')
    );
    setValidationErrors(filteredErrors);
  };
  
  // Add a state change effect to log when businessData changes
  useEffect(() => {
    if (Object.keys(businessData).length > 0) {
      console.log('\n=== COMPONENT: BUSINESS DATA STATE UPDATED ===');
      console.log('Business Data:', businessData);
      console.log('Federal Tax ID:', businessData.FederalTaxID);
    }
  }, [businessData]);
  
  // Update the useEffect for initial validation to properly check dropdown fields
  // Validate initial data once business data and fields are loaded
  useEffect(() => {
    if (!loading && businessFields.length > 0 && Object.keys(businessData).length > 0) {
      console.log("Running initial validation check on loaded data");
      console.log("Business data:", businessData);
      console.log("Business fields count:", businessFields.length);
      
      // Force validation of all fields immediately, including dropdowns
      setShowValidationErrors(true);
      
      // Run validation immediately
      console.log("Running validateForm immediately...");
      const isValid = validateForm();
      console.log("Initial validation result:", isValid);
      updateCompletionStats();
    }
  }, [loading, businessFields, businessData]);
  
  // Separate useEffect to run validation after showValidationErrors is updated
  useEffect(() => {
    if (showValidationErrors && !loading && businessFields.length > 0 && Object.keys(businessData).length > 0) {
      console.log("Re-running validation after showValidationErrors was set to true");
      const isValid = validateForm();
      console.log("Validation result after showValidationErrors=true:", isValid);
      console.log("Current validation errors:", validationErrors);
    }
  }, [showValidationErrors, loading, businessFields, businessData]);
  
  // Update component when validation errors change
  useEffect(() => {
    // Update canSubmitToLenders based on validation errors AND file uploads
    setCanSubmitToLenders(validationErrors.length === 0 && fileUploadsComplete);
    
    // Also update completion stats
    if (businessFields.length > 0) {
      updateCompletionStats();
    }
    
    // Log validation errors for debugging
    if (validationErrors.length > 0) {
      console.log('Current validation errors:', validationErrors);
    }
  }, [validationErrors, businessFields, fileUploadsComplete]);
  
  // Check if file upload requirements are met
  useEffect(() => {
    if (activeFinancingTab === 'equipment-financing') {
      // For equipment financing: need application, quotes, and credit
      const appFilesUploaded = appFiles.length > 0 ? 1 : 0;
      const quoteFilesUploaded = quoteFiles.length > 0 ? 1 : 0;
      const creditFilesUploaded = creditFiles.length > 0 ? 1 : 0;
      const totalUploaded = appFilesUploaded + quoteFilesUploaded + creditFilesUploaded;
      
      setFileUploadStats({
        completed: totalUploaded,
        total: 3
      });
      
      setFileUploadsComplete(totalUploaded === 3);
    } else if (activeFinancingTab === 'real-estate') {
      // For real estate: need application and asset form
      const appFilesUploaded = appFiles.length > 0 ? 1 : 0;
      const assetFormFilesUploaded = assetFormFiles.length > 0 ? 1 : 0;
      const totalUploaded = appFilesUploaded + assetFormFilesUploaded;
      
      setFileUploadStats({
        completed: totalUploaded,
        total: 2
      });
      
      setFileUploadsComplete(totalUploaded === 2);
    } else {
      // For working capital and SBA: need bank statements and application
      const bankFilesUploaded = bankFiles.length > 0 ? 1 : 0;
      const appFilesUploaded = appFiles.length > 0 ? 1 : 0;
      const totalUploaded = bankFilesUploaded + appFilesUploaded;
      
      setFileUploadStats({
        completed: totalUploaded,
        total: 2
      });
      
      setFileUploadsComplete(totalUploaded === 2);
    }
  }, [bankFiles, appFiles, quoteFiles, creditFiles, assetFormFiles, activeFinancingTab]);
  
  // Calculate completion statistics for each category
  const updateCompletionStats = () => {
    const stats = {};
    
    // For each category, count completed fields
    categories.forEach(category => {
      // Get fields that are actually rendered in this category
      // This will exclude fields that might be categorized but not displayed
      let fieldsToCount = [];
      
      if (category === 'Business Information') {
        // Get base business fields (excluding specific financial fields that are shown separately)
        // These are the fields actually rendered on the Business Info tab
        const categoryFields = businessFields.filter(field => 
          getMappedCategory(field.category) === category &&
          !['AnnualRevenue', 'AverageMonthlySales', 'RequestedAmount', 'AverageBankBalance',
            'AverageNSFs', 'AverageNumberOfDeposits', 'CumulativeOwnershipPercentage'].includes(field.name)
        );
        
        // Add the financial fields that are actually rendered
        const requiredFinancialFields = [
          'AnnualRevenue', 'AverageMonthlySales', 'RequestedAmount', 'AverageBankBalance',
          'AverageNSFs', 'AverageNumberOfNegDays', 'AverageNumberOfDeposits'
        ];
        
        // Optional fields that should be counted in the total but not required
        const optionalFinancialFields = [
          'NumberOfPositions', 'PayoffAmountNeeded'
        ];
        
        // Map the required fields to field-like objects for consistency
        const requiredFieldObjects = requiredFinancialFields.map(name => ({
          name,
          category: 'Financial Information',
          label: name.replace(/([A-Z])/g, ' $1').trim(), // Simple formatter for label
          isRequired: true
        }));
        
        // Map the optional fields to field-like objects
        const optionalFieldObjects = optionalFinancialFields.map(name => ({
          name,
          category: 'Financial Information',
          label: name.replace(/([A-Z])/g, ' $1').trim(), // Simple formatter for label
          isRequired: false
        }));
        
        // Combine them - this represents all fields actually displayed
        fieldsToCount = [...categoryFields, ...requiredFieldObjects, ...optionalFieldObjects];
      } else {
        // For other categories, just get fields in that category
        fieldsToCount = businessFields.filter(field => 
          getMappedCategory(field.category) === category &&
          // Exclude CumulativeOwnershipPercentage from all categories
          field.name !== 'CumulativeOwnershipPercentage'
        );
      }
      
      let completedFields = 0;
      let totalFields = 0;
      let requiredFields = 0;
      let requiredCompleted = 0;
      
      // Count both required and optional fields that are actually displayed
      fieldsToCount.forEach(field => {
        // Check if this field is required
        // For fields with explicit isRequired property, use that
        // Otherwise, check if it's a credit score field, average NSFs, average number of deposits, or optional bank statement fields
        const isRequired = field.isRequired !== undefined ? 
          field.isRequired : 
          !field.name.toLowerCase().includes('creditscore') && 
          field.name !== 'AverageNSFs' && 
          field.name !== 'AverageNumberOfDeposits' &&
          !isOptionalBankStatementField(field.name);
        
        // Add to total count for all fields
        totalFields++;
        
        // Track required fields separately
        if (isRequired) {
          requiredFields++;
        }
        
        const value = businessData[field.name];
        const isEmpty = value === undefined || value === null || 
          (typeof value !== 'number' && value.toString().trim() === '');
        
        // Check if field has a validation error
        const hasError = validationErrors.some(error => error.field === field.name);
        
        // A field is completed if it has a value AND doesn't have validation errors
        if (!isEmpty && !hasError) {
          completedFields++;
          
          // Track completed required fields separately
          if (isRequired) {
            requiredCompleted++;
          }
        }
      });
      
      // Special case for Contact Information - include secondary contact fields if present
      if (category === 'Contact Information' && hasSecondaryContact) {
        // Add standard secondary contact fields
        const secondaryContactFieldNames = [
          'Contact2FirstName', 'Contact2LastName', 'Contact2Title', 'Contact2SSN',
          'Contact2DateOfBirth', 'Contact2Email', 'Contact2CellPhone',
          'Contact2OwnershipPercentage', 'Contact2Street', 'Contact2City',
          'Contact2State', 'Contact2ZipCode'
        ];
        
        // Also add optional fields like Credit Score
        const optionalSecondaryFields = ['Contact2CreditScore'];
        
        // Count total required fields
        const requiredSecondaryFields = secondaryContactFieldNames.filter(
          fieldName => !fieldName.toLowerCase().includes('creditscore')
        );
        
        // Add to totals
        totalFields += secondaryContactFieldNames.length + optionalSecondaryFields.length;
        requiredFields += requiredSecondaryFields.length;
        
        // Count completed fields
        [...secondaryContactFieldNames, ...optionalSecondaryFields].forEach(fieldName => {
          const isRequired = !fieldName.toLowerCase().includes('creditscore');
          
          const value = businessData[fieldName];
          const isEmpty = value === undefined || value === null || 
            (typeof value !== 'number' && value.toString().trim() === '');
          
          // Check if field has a validation error
          const hasError = validationErrors.some(error => error.field === fieldName);
          
          let isValid = !isEmpty && !hasError;
          
          // A field is completed if it has a value AND doesn't have validation errors
          if (isValid) {
            completedFields++;
            
            // Track completed required fields separately
            if (isRequired) {
              requiredCompleted++;
            }
          }
        });
      }
      
      stats[category] = {
        completed: completedFields,
        total: totalFields,
        requiredCompleted: requiredCompleted,
        requiredTotal: requiredFields,
        // All required fields are completed
        allRequiredCompleted: requiredCompleted === requiredFields
      };
    });
    
    console.log('Updated completion stats with validation check:', stats);
    setCompletionStats(stats);
  };
  
  // Helper function to remap categories without changing field properties
  const getMappedCategory = (originalCategory) => {
    // Map primary contact and contact address categories to Contact Information
    if (originalCategory === 'Primary Contact' || originalCategory === 'Contact Address') {
      return 'Contact Information';
    }
    
    // Map business categories to Business Information
    if (originalCategory === 'Account Information' || originalCategory === 'Business' || originalCategory === 'Business Address') {
      return 'Business Information';
    }
    
    // Map Additional Details to General to avoid having a separate tab
    if (originalCategory === 'Additional Details') {
      return 'General';
    }
    
    // Map financial fields to Financial Information
    if (originalCategory === 'Financial Information') {
      return 'Financial Information';
    }
    
    // Keep Bank Statement Data as its own category
    if (originalCategory === 'Bank Statement Data') {
      return 'Bank Statement Data';
    }
    
    // HIDE Bank Statement Aggregates from the form - these are virtual fields only used for lender criteria
    // They should not appear as form fields since they are computed from the Bank Statement Data fields
    if (originalCategory === 'Bank Statement Aggregates') {
      return null; // Return null to filter out this category
    }
    
    // Keep other categories as is
    return originalCategory;
  };
  
  // Enhanced validateForm function to properly check dropdowns
  const validateForm = () => {
    if (!businessFields || categories.length === 0) return true;
    
    let isValid = true;
    const errors = [];
    
         console.log("validateForm called with showValidationErrors:", showValidationErrors);
     console.log("Current businessData:", Object.keys(businessData));
     console.log("State field values:", {
       BusinessState: businessData.BusinessState,
       ContactState: businessData.ContactState,
       EntityType: businessData.EntityType,
       Contact2State: businessData.Contact2State
     });
     
     // Check each field with its mapped category
    businessFields.forEach(field => {
      const mappedCategory = getMappedCategory(field.category);
      
      // Skip virtual fields (like Bank Statement Aggregates) - they have null mapped category
      // These are computed fields only used for lender criteria, not form validation
      if (mappedCategory === null) {
        return;
      }
      
      const value = businessData[field.name];
      // Fix for numerical zero values being considered empty
      // For dropdown fields, also treat empty string as empty (handles "Select State..." case)
      const isEmpty = value === undefined || value === null || 
        (typeof value !== 'number' && value.toString().trim() === '');
      
      // Check if this field is required (all fields except credit score, average NSFs, average number of neg days, average number of deposits, and optional bank statement fields)
      const isRequired = !field.name.toLowerCase().includes('creditscore') && 
                         field.name !== 'AverageNSFs' && 
                         field.name !== 'AverageNumberOfNegDays' && 
                         field.name !== 'AverageNumberOfDeposits' &&
                         !isOptionalBankStatementField(field.name);
      
      // Special attention to dropdown fields
      const isDropdown = field.name === 'EntityType' || 
                         field.name === 'BusinessState' || field.name === 'ContactState' || 
                         field.name === 'Contact2State';
      
      // Debug logging for state fields
      if (isDropdown) {
        console.log(`Dropdown field ${field.name}: value="${value}", isEmpty=${isEmpty}, isRequired=${isRequired}`);
      }
      
      // Special case for Title field - auto-populate with "Owner" if empty
      if (field.name === 'ContactTitle' && isEmpty) {
        // Update business data with default "Owner" value
        setBusinessData(prev => ({
          ...prev,
          ContactTitle: "Owner"
        }));
        
        // Don't mark as error since we've auto-populated it
        return;
      }
      
      // Get the validator for this field
      const validator = getValidatorForField(field);
      
      // First check if the field has a specific validator
      if (validator && validator.validate) {
        // For credit score, only validate if it has a value
        if (field.name.toLowerCase().includes('creditscore')) {
          if (!isEmpty) {
            const validationResult = validator.validate(value);
            if (!validationResult.isValid) {
              isValid = false;
              errors.push({
                field: field.name,
                category: mappedCategory,
                message: `${field.label}: ${validationResult.message}`
              });
            }
          }
        } else {
          // For all other fields, apply validator regardless of empty or not
          const validationResult = validator.validate(value);
          if (!validationResult.isValid) {
            isValid = false;
            errors.push({
              field: field.name,
              category: mappedCategory,
              message: `${field.label}: ${validationResult.message}`
            });
          }
        }
      } 
      // If no validator or validation passed, check if it's empty and required
      else if (isEmpty && isRequired) {
        isValid = false;
        
        // Special message for dropdown fields
        let message = `${field.label} is required`;
        if (isDropdown) {
          message = `Please select a ${field.label.toLowerCase()}`;
        }
        
        errors.push({
          field: field.name,
          category: mappedCategory,
          message: message
        });
      }
    });
    
         // Add validation for hardcoded financial fields that are required
     const requiredFinancialFields = [
       { name: 'AnnualRevenue', label: 'Annual Revenue' },
       { name: 'AverageMonthlySales', label: 'Average Monthly Sales' },
       { name: 'RequestedAmount', label: 'Requested Amount' },
       { name: 'AverageBankBalance', label: 'Average Bank Balance' }
     ];
     
     requiredFinancialFields.forEach(fieldInfo => {
       const value = businessData[fieldInfo.name];
       const isEmpty = value === undefined || value === null || value === '' || 
         (typeof value === 'string' && value.trim() === '') ||
         (typeof value === 'number' && isNaN(value));
       
       const invalidRequestedAmount = fieldInfo.name === 'RequestedAmount' && !isValidRequestedAmount(value);
       if (isEmpty || invalidRequestedAmount) {
         isValid = false;
         errors.push({
           field: fieldInfo.name,
           category: 'Business Information',
           message: invalidRequestedAmount
             ? 'Requested Amount must be a valid number greater than zero'
             : `${fieldInfo.label} is required`
         });
       }
     });
     
     
    
    // If secondary contact is active, validate those fields as well
    if (hasSecondaryContact) {
      // Fetch Contact2 fields from the field mappings database if they exist, 
      // otherwise fall back to a hard-coded list
      const getContact2Fields = async () => {
        try {
          const response = await axios.get('/api/field-mappings');
          const mappings = response.data;
          
          // Get all secondary contact fields from mappings
          const secondaryContactFields = mappings.filter(mapping => 
            mapping.opportunityField.startsWith('Contact2') && 
            mapping.salesforceObject === 'Contact'
          );
          
          return secondaryContactFields.map(mapping => ({
            name: mapping.opportunityField,
            label: mapping.opportunityField.replace('Contact2', 'Secondary '),
            type: mapping.fieldType || 'string',
            category: 'Contact Information',
            validator: getValidatorForField({
              name: mapping.opportunityField,
              label: mapping.opportunityField.replace('Contact2', 'Secondary ')
            })
          }));
        } catch (error) {
          console.error('Error fetching secondary contact field mappings:', error);
          // Return default fields if fetch fails
          return [
            { name: 'Contact2FirstName', label: 'Secondary First Name', type: 'string', category: 'Contact Information' },
            { name: 'Contact2LastName', label: 'Secondary Last Name', type: 'string', category: 'Contact Information' },
            { name: 'Contact2Title', label: 'Secondary Title', type: 'string', category: 'Contact Information' },
            { name: 'Contact2SSN', label: 'Secondary SSN', type: 'string', category: 'Contact Information', validator: validateSSN },
            { name: 'Contact2DateOfBirth', label: 'Secondary Date of Birth', type: 'date', category: 'Contact Information' },
            { name: 'Contact2Email', label: 'Secondary Email', type: 'string', category: 'Contact Information', validator: validateEmail },
            { name: 'Contact2CellPhone', label: 'Secondary Cell Phone', type: 'string', category: 'Contact Information', validator: validatePhone },
            { name: 'Contact2OwnershipPercentage', label: 'Secondary Ownership Percentage', type: 'string', category: 'Contact Information' },
            { name: 'Contact2CreditScore', label: 'Secondary Credit Score', type: 'string', category: 'Contact Information', validator: validateCreditScore },
            { name: 'Contact2Street', label: 'Secondary Street', type: 'string', category: 'Contact Information' },
            { name: 'Contact2City', label: 'Secondary City', type: 'string', category: 'Contact Information' },
            { name: 'Contact2State', label: 'Secondary State', type: 'string', category: 'Contact Information' },
            { name: 'Contact2ZipCode', label: 'Secondary Zip/Postal Code', type: 'string', category: 'Contact Information', validator: validateZip },
            { name: 'Contact2Country', label: 'Secondary Country', type: 'string', category: 'Contact Information' }
          ];
        }
      };
      
      // Get secondary contact fields - note: we need to handle this asynchronously
      // We'll set up a flag to rerun validation if fields change
      let secondaryFieldsValidationPending = true;
      
      getContact2Fields().then(secondaryContactFields => {
        // Validate each secondary contact field
        secondaryContactFields.forEach(field => {
          const value = businessData[field.name];
          const isEmpty = value === undefined || value === null || 
            (typeof value !== 'number' && value.toString().trim() === '');
          
          // Check if this field is required (all fields except credit score, average NSFs, average number of neg days, average number of deposits, and optional bank statement fields)
          const isRequired = !field.name.toLowerCase().includes('creditscore') && 
                             field.name !== 'AverageNSFs' && 
                             field.name !== 'AverageNumberOfNegDays' && 
                             field.name !== 'AverageNumberOfDeposits' &&
                             !isOptionalBankStatementField(field.name);
          
          // Validate required fields
          if (isEmpty && isRequired) {
            isValid = false;
            errors.push({
              field: field.name,
              category: 'Contact Information',
              message: `${field.label.replace('Secondary', 'Second Contact')} is required`
            });
          } else if (!isEmpty) {
            // Apply field-specific validation if a validator is specified and the field has a value
            if (field.validator && field.validator.validate) {
              const validationResult = field.validator.validate(value);
              if (!validationResult.isValid) {
                isValid = false;
                errors.push({
                  field: field.name,
                  category: 'Contact Information',
                  message: `${field.label.replace('Secondary', 'Second Contact')}: ${validationResult.message}`
                });
              }
            }
          }
        });
        
        // Update validation errors at the end of async validation
        setValidationErrors(errors);
        setCanSubmitToLenders(isValid);
        secondaryFieldsValidationPending = false;
      });
      
      // For now, we'll use a simpler approach and still use a hardcoded list for immediate validation
      // This ensures the UI is responsive while we wait for the async operation
      const defaultFields = [
        { name: 'Contact2FirstName', label: 'Secondary First Name', type: 'string', category: 'Contact Information' },
        { name: 'Contact2LastName', label: 'Secondary Last Name', type: 'string', category: 'Contact Information' },
        { name: 'Contact2Title', label: 'Secondary Title', type: 'string', category: 'Contact Information' },
        { name: 'Contact2SSN', label: 'Secondary SSN', type: 'string', category: 'Contact Information', validator: validateSSN },
        { name: 'Contact2DateOfBirth', label: 'Secondary Date of Birth', type: 'date', category: 'Contact Information' },
        { name: 'Contact2Email', label: 'Secondary Email', type: 'string', category: 'Contact Information', validator: validateEmail },
        { name: 'Contact2CellPhone', label: 'Secondary Cell Phone', type: 'string', category: 'Contact Information', validator: validatePhone },
        { name: 'Contact2OwnershipPercentage', label: 'Secondary Ownership Percentage', type: 'string', category: 'Contact Information' },
        { name: 'Contact2CreditScore', label: 'Secondary Credit Score', type: 'string', category: 'Contact Information', validator: validateCreditScore },
        { name: 'Contact2Street', label: 'Secondary Street', type: 'string', category: 'Contact Information' },
        { name: 'Contact2City', label: 'Secondary City', type: 'string', category: 'Contact Information' },
        { name: 'Contact2State', label: 'Secondary State', type: 'string', category: 'Contact Information' },
        { name: 'Contact2ZipCode', label: 'Secondary Zip/Postal Code', type: 'string', category: 'Contact Information', validator: validateZip },
        { name: 'Contact2Country', label: 'Secondary Country', type: 'string', category: 'Contact Information' }
      ];
      
      // Immediate validation with default fields
      defaultFields.forEach(field => {
        const value = businessData[field.name];
        const isEmpty = value === undefined || value === null || 
          (typeof value !== 'number' && value.toString().trim() === '');
        
        // Check if this field is required (all fields except credit score, average NSFs, average number of deposits, and optional bank statement fields)
        const isRequired = !field.name.toLowerCase().includes('creditscore') && 
                           field.name !== 'AverageNSFs' && 
                           field.name !== 'AverageNumberOfDeposits' &&
                           !isOptionalBankStatementField(field.name);
        
        // All required fields must have a value
        if (isEmpty && isRequired) {
          isValid = false;
          errors.push({
            field: field.name,
            category: 'Contact Information',
            message: `${field.label.replace('Secondary', 'Second Contact')} is required`
          });
        } else if (!isEmpty) {
          // Apply field-specific validation if a validator is specified and the field has a value
          if (field.validator && field.validator.validate) {
            const validationResult = field.validator.validate(value);
            if (!validationResult.isValid) {
              isValid = false;
              errors.push({
                field: field.name,
                category: 'Contact Information',
                message: `${field.label.replace('Secondary', 'Second Contact')}: ${validationResult.message}`
              });
            }
          }
        }
      });
    }
    
         console.log('Validation errors found:', errors);
     console.log('Setting validation errors in state...');
     setValidationErrors(errors);
     setCanSubmitToLenders(isValid);
     
     // Update completion stats after validation is done
     updateCompletionStats();
     
     console.log('validateForm returning isValid:', isValid);
     return isValid;
  };
  
  // Handle form input changes
  const handleInputChange = (field, value) => {
    let fieldName, fieldValue;
    
    // Extract field name and value
    if (typeof field === 'object' && field.target) {
      fieldName = field.target.name;
      fieldValue = field.target.value;
    } else {
      fieldName = field;
      fieldValue = value;
    }
    
    // Handle special cases where fieldValue might be undefined/null
    if (fieldValue === undefined || fieldValue === null) {
      fieldValue = '';
    }
    
    // Special handling for specific fields
    if (fieldName === 'FederalTaxID') {
      // Apply EIN formatting as the user types
      fieldValue = validateEIN.format(fieldValue);
    } else if (fieldName === 'ContactOwnershipPercentage' || fieldName === 'Contact2OwnershipPercentage') {
      // Apply percentage formatting as the user types
      fieldValue = validatePercentage.format(fieldValue);
    } else if (fieldName === 'BusinessState' || fieldName === 'ContactState' || fieldName === 'Contact2State') {
      // Ensure state values are always uppercase to match dropdown options
      fieldValue = fieldValue.toUpperCase();
    } else if (fieldName === 'ContactCellPhone' || fieldName === 'Contact2CellPhone') {
      // Format phone number correctly (this will handle +1 appropriately)
      fieldValue = validatePhone.format(fieldValue);
    } else if (isWholeNumberField(fieldName)) {
      // For fields that should only accept whole numbers, truncate any decimals
      fieldValue = convertToWholeNumber(fieldValue);
    }
    
    // Update business data immediately
    setBusinessData(prevData => {
      const newData = {
        ...prevData,
        [fieldName]: fieldValue
      };
      
      // Recalculate Average Monthly Sales if a deposit field changed
      const depositFields = ['Month1Deposits', 'Month2Deposits', 'Month3Deposits', 'Month4Deposits'];
      if (depositFields.includes(fieldName)) {
        // Month 1-3 are always included, Month 4 only if non-zero
        const month1 = parseFloat(newData.Month1Deposits) || 0;
        const month2 = parseFloat(newData.Month2Deposits) || 0;
        const month3 = parseFloat(newData.Month3Deposits) || 0;
        const month4 = parseFloat(newData.Month4Deposits) || 0;
        
        // Always use months 1-3
        let totalDeposits = month1 + month2 + month3;
        let monthCount = 3;
        
        // Only include month 4 if it has a non-zero value
        if (month4 > 0) {
          totalDeposits += month4;
          monthCount = 4;
        }
        
        const rawAverage = monthCount > 0 ? totalDeposits / monthCount : 0;
        newData.AverageMonthlySales = rawAverage.toFixed(2);
      }
      
      return newData;
    });
    
    // Safely check if a field has a value - ensure we're working with a string
    const hasValue = typeof fieldValue === 'string' && fieldValue.trim() !== '' || 
                     typeof fieldValue === 'number' && !isNaN(fieldValue);
    
    if (hasValue) {
      // Remove this field from validation errors if it's there
      const hasError = validationErrors.some(err => err.field === fieldName);
      if (hasError) {
        setValidationErrors(prev => 
          prev.filter(err => err.field !== fieldName)
        );
      }
      
      // Special validation for Federal Tax ID
      if (fieldName === 'FederalTaxID') {
        const validationResult = validateEIN.validate(fieldValue);
        if (!validationResult.isValid) {
          setValidationErrors(prev => {
            // Remove any existing error for this field
            const filtered = prev.filter(err => err.field !== fieldName);
            // Find field info to get the proper label
            const fieldInfo = businessFields.find(f => f.name === fieldName) || 
                          { name: fieldName, label: 'Federal Tax ID' };
            const category = fieldInfo.category || 'General';
            const mappedCategory = getMappedCategory(category);
            // Add the new error
            return [...filtered, {
              field: fieldName,
              category: mappedCategory,
              message: `${fieldInfo.label}: ${validationResult.message}`
            }];
          });
        }
      }
      
      // Special validation for DateOfBirth fields
      if (fieldName === 'ContactDateOfBirth' || fieldName === 'Contact2DateOfBirth') {
        const validationResult = validateDateOfBirth.validate(value);
        if (!validationResult.isValid) {
          setValidationErrors(prev => {
            // Remove any existing error for this field
            const filtered = prev.filter(err => err.field !== fieldName);
            // Find field info to get the proper label
            const fieldInfo = businessFields.find(f => f.name === fieldName) || 
                          { name: fieldName, label: fieldName.replace(/([A-Z])/g, ' $1').trim() };
            const category = fieldInfo.category || 'General';
            const mappedCategory = getMappedCategory(category);
            // Add the new error
            return [...filtered, {
              field: fieldName,
              category: mappedCategory,
              message: `${fieldInfo.label}: ${validationResult.message}`
            }];
          });
        }
      }
      
      // Special validation for Ownership Percentage fields
      if (fieldName === 'ContactOwnershipPercentage' || fieldName === 'Contact2OwnershipPercentage') {
        const validationResult = validatePercentage.validate(fieldValue);
        if (!validationResult.isValid) {
          setValidationErrors(prev => {
            // Remove any existing error for this field
            const filtered = prev.filter(err => err.field !== fieldName);
            // Find field info to get the proper label
            const fieldInfo = businessFields.find(f => f.name === fieldName) || 
                          { name: fieldName, label: fieldName.replace(/([A-Z])/g, ' $1').trim() };
            const category = fieldInfo.category || 'General';
            const mappedCategory = getMappedCategory(category);
            // Add the new error
            return [...filtered, {
              field: fieldName,
              category: mappedCategory,
              message: `${fieldInfo.label}: ${validationResult.message}`
            }];
          });
        }
      }
    }
    
    // Always check if field is empty since all fields are required (except credit score, average NSFs, average number of neg days, average number of deposits, and optional bank statement fields)
    const isCreditScoreField = fieldName.toLowerCase().includes('creditscore');
    const isOptionalField = fieldName === 'AverageNSFs' || fieldName === 'AverageNumberOfNegDays' || fieldName === 'AverageNumberOfDeposits' || isOptionalBankStatementField(fieldName);
    const isEmpty = fieldValue === null || fieldValue === undefined || 
                    (typeof fieldValue !== 'number' && String(fieldValue).trim() === '');
  
    if (isEmpty && !isCreditScoreField && !isOptionalField) {
      // If field is cleared and not a credit score field, immediately validate and show error
      setShowValidationErrors(true);
      
      // Add this specific field error immediately
      const existingErrorIndex = validationErrors.findIndex(err => err.field === fieldName);
      
      if (existingErrorIndex === -1) {
        // Find field info to get the proper label
        const fieldInfo = businessFields.find(f => f.name === fieldName) || 
                       { name: fieldName, label: fieldName.replace(/([A-Z])/g, ' $1').trim() };
        
        const category = fieldInfo.category || 'General';
        const mappedCategory = getMappedCategory(category);
        
        // Add error for this field
        setValidationErrors(prev => [
          ...prev,
          {
            field: fieldName,
            category: mappedCategory,
            message: `${fieldInfo.label} is required`
          }
        ]);
      }
    } else if (isCreditScoreField && hasValue) {
      // For credit score field, only validate if it has a value
      const fieldInfo = businessFields.find(f => f.name === fieldName);
      if (fieldInfo) {
        const validator = validateCreditScore;
        if (validator && validator.validate) {
          const validationResult = validator.validate(fieldValue);
          if (!validationResult.isValid) {
            // Update just this field's error
            const mappedCategory = getMappedCategory(fieldInfo.category);
            
            const existingErrorIndex = validationErrors.findIndex(err => err.field === fieldName);
            if (existingErrorIndex !== -1) {
              // Update existing error
              setValidationErrors(prev => {
                const updated = [...prev];
                updated[existingErrorIndex] = {
                  field: fieldName,
                  category: mappedCategory,
                  message: `${fieldInfo.label}: ${validationResult.message}`
                };
                return updated;
              });
            } else {
              // Add new error
              setValidationErrors(prev => [
                ...prev,
                {
                  field: fieldName,
                  category: mappedCategory,
                  message: `${fieldInfo.label}: ${validationResult.message}`
                }
              ]);
            }
          }
        }
      }
    } else {
      // If field has content and we're showing validation, validate only this field
      if (showValidationErrors) {
        const fieldInfo = businessFields.find(f => f.name === fieldName);
        if (fieldInfo) {
          const validator = getValidatorForField(fieldInfo);
          if (validator && validator.validate) {
            const validationResult = validator.validate(fieldValue);
            if (!validationResult.isValid) {
              // Update just this field's error
              const mappedCategory = getMappedCategory(fieldInfo.category);
              
              const existingErrorIndex = validationErrors.findIndex(err => err.field === fieldName);
              if (existingErrorIndex !== -1) {
                // Update existing error
                setValidationErrors(prev => {
                  const updated = [...prev];
                  updated[existingErrorIndex] = {
                    field: fieldName,
                    category: mappedCategory,
                    message: `${fieldInfo.label}: ${validationResult.message}`
                  };
                  return updated;
                });
              } else {
                // Add new error
                setValidationErrors(prev => [
                  ...prev,
                  {
                    field: fieldName,
                    category: mappedCategory,
                    message: `${fieldInfo.label}: ${validationResult.message}`
                  }
                ]);
              }
            }
          }
        }
      }
    }
    
    // Update completion stats after handling validation
    updateCompletionStats();
  };
  
  // Add this function to handle phone input with proper cursor position tracking
  const handlePhoneInputChange = (event) => {
    const input = event.target;
    const fieldName = input.name;
    const oldValue = input.value;
    const selectionStart = input.selectionStart;
    
    // Count the number of format characters (non-digits) before the cursor
    const formatCharsPrev = (oldValue.substring(0, selectionStart).match(/[^\d]/g) || []).length;
    
    // Store the raw digits before formatting
    const oldDigits = oldValue.replace(/\D/g, '');
    const hasCountryCode = oldValue.startsWith('+1');
    
    // Apply formatting through the validatePhone utility
    const newValue = validatePhone.format(oldValue);
    
    // Update the state with the new formatted value
    setBusinessData(prevData => ({
      ...prevData,
      [fieldName]: newValue
    }));
    
    // Schedule cursor repositioning after React re-render
    setTimeout(() => {
      // Try to find the right position for the cursor
      if (input) {
        // Count the formatting characters in the new value
        const formatCharsNew = (newValue.substring(0, selectionStart).match(/[^\d]/g) || []).length;
        const formatCharsDiff = formatCharsNew - formatCharsPrev;
        
        // Calculate the new cursor position, accounting for format characters
        let newPosition = selectionStart + formatCharsDiff;
        
        // Handle special cases when deleting a digit
        if (newValue.length < oldValue.length && selectionStart !== oldValue.length) {
          // If a digit was deleted, adjust the position
          newPosition = selectionStart;
        }
        
        // Ensure cursor position is within bounds
        newPosition = Math.max(0, Math.min(newPosition, newValue.length));
        
        // Set the cursor position
        input.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
    
    // Validate if needed and update error states
    const isEmpty = newValue === undefined || newValue === null || newValue === '';
    if (!isEmpty) {
      const validationResult = validatePhone.validate(newValue);
      if (!validationResult.isValid) {
        // Add or update validation error
        setValidationErrors(prev => {
          const existingErrorIndex = prev.findIndex(err => err.field === fieldName);
          const fieldInfo = businessFields.find(f => f.name === fieldName) || 
                         { name: fieldName, label: fieldName.replace(/([A-Z])/g, ' $1').trim() };
          const category = fieldInfo.category || 'General';
          const mappedCategory = getMappedCategory(category);
          
          const errorEntry = {
            field: fieldName,
            category: mappedCategory,
            message: `${fieldInfo.label}: ${validationResult.message}`
          };
          
          if (existingErrorIndex !== -1) {
            // Update existing error
            const updated = [...prev];
            updated[existingErrorIndex] = errorEntry;
            return updated;
          } else {
            // Add new error
            return [...prev, errorEntry];
          }
        });
      } else {
        // Remove any existing error for this field
        setValidationErrors(prev => prev.filter(err => err.field !== fieldName));
      }
    }
    
    // Update completion stats
    updateCompletionStats();
  };
  
  // Handle sync with CRM
  const handleSyncWithCRM = async () => {
    // Validate form first
    const isValid = validateForm();
    if (!isValid) {
      setShowValidationErrors(true);
      notifyError(
        'Please fix all validation errors before syncing with Salesforce.', 
        'Validation Error'
      );
      return;
    }
    
    try {
      setSyncLoading(true);
      setSyncStatus(null);
      
      // Normalize data before sending to API
      const normalizedData = { ...businessData };
      
      // Apply field-specific normalization to primary contact fields
      businessFields.forEach(field => {
        const validator = getValidatorForField(field);
        if (validator && validator.normalize && normalizedData[field.name] !== undefined) {
          normalizedData[field.name] = validator.normalize(normalizedData[field.name]);
        }
      });
      
      // Apply normalization to secondary contact fields if present
      if (hasSecondaryContact) {
        // Define field validators
        const secondaryFieldValidators = {
          'Contact2SSN': validateSSN,
          'Contact2Email': validateEmail,
          'Contact2CellPhone': validatePhone,
          'Contact2ZipCode': validateZip
        };
        
        // Apply normalization
        Object.entries(secondaryFieldValidators).forEach(([fieldName, validator]) => {
          if (normalizedData[fieldName] !== undefined && validator.normalize) {
            normalizedData[fieldName] = validator.normalize(normalizedData[fieldName]);
          }
        });
        
        // Add a flag to indicate secondary contact presence
        normalizedData.hasSecondaryContact = true;
      } else {
        normalizedData.hasSecondaryContact = false;
      }
      
      console.log('Syncing form data with Salesforce CRM:', normalizedData);
      
      // Update Salesforce opportunity with form data
      const result = await updateOpportunityWithFormData(opportunityId, normalizedData);
      
      console.log('Sync result:', result);
      
      if (result.success) {
        setSyncStatus({
          type: 'success',
          message: 'Successfully synced data with Salesforce CRM.'
        });
        
        // Update contact data with the updated contact
        if (result.updatedContact) {
          setContactData(result.updatedContact);
        }
        
        // Send success notification
        success(
          'Opportunity data successfully synced with Salesforce CRM.',
          'Sync Successful'
        );
      } else {
        setSyncStatus({
          type: 'warning',
          message: result.message || 'No changes were made to the Salesforce opportunity.'
        });
        
        // Send warning notification
        warning(
          result.message || 'No changes were made to the Salesforce opportunity.',
          'Sync Warning'
        );
      }
    } catch (error) {
      console.error('Error syncing with CRM:', error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to sync with Salesforce CRM.';
      
      setSyncStatus({
        type: 'error',
        message: errorMessage
      });
      
      // Send error notification
      notifyError(
        errorMessage,
        'Sync Failed'
      );
    } finally {
      setSyncLoading(false);
    }
  };
  
  // Helper to determine if a bank statement field is required
  // Month 1-3 Deposits and Deposit Counts are required (need at least 3 months of data)
  // Month 4 everything + ALL NSFs + ALL Negative Days are optional
  const isRequiredBankStatementField = (fieldName) => {
    const requiredBankFields = [
      'Month1Deposits', 'Month2Deposits', 'Month3Deposits',
      'Month1DepositCount', 'Month2DepositCount', 'Month3DepositCount'
    ];
    return requiredBankFields.includes(fieldName);
  };

  const isOptionalBankStatementField = (fieldName) => {
    const optionalBankFields = [
      'Month4Deposits', 'Month4DepositCount',
      'Month1NSFs', 'Month2NSFs', 'Month3NSFs', 'Month4NSFs',
      'Month1NegativeDays', 'Month2NegativeDays', 'Month3NegativeDays', 'Month4NegativeDays'
    ];
    return optionalBankFields.includes(fieldName);
  };

  // Check if field should be treated as a numerical value
  const isNumericalField = (fieldName) => {
    const numericalFields = [
      'AnnualRevenue', 'BusinessStartDate', 'AverageBankBalance', 
      'RequestedAmount', 'ContactOwnershipPercentage', 'ContactCreditScore',
      'Contact2OwnershipPercentage', 'Contact2CreditScore',
      'NumberOfPositions', 'PayoffAmountNeeded', 'AverageMonthlySales',
      'AverageNSFs', 'AverageNumberOfNegDays', 'AverageNumberOfDeposits',
      // Bank Statement Data fields
      'Month1Deposits', 'Month2Deposits', 'Month3Deposits', 'Month4Deposits',
      'Month1NSFs', 'Month2NSFs', 'Month3NSFs', 'Month4NSFs',
      'Month1NegativeDays', 'Month2NegativeDays', 'Month3NegativeDays', 'Month4NegativeDays',
      'Month1DepositCount', 'Month2DepositCount', 'Month3DepositCount', 'Month4DepositCount'
    ];
    return numericalFields.includes(fieldName);
  };

  // Check if field should only accept whole numbers (no decimals)
  const isWholeNumberField = (fieldName) => {
    const wholeNumberFields = [
      'AnnualRevenue', 'RequestedAmount', 
      'NumberOfPositions', 'PayoffAmountNeeded',
      // Bank Statement Data fields - NSFs, Negative Days, and Deposit Counts are whole numbers
      'Month1NSFs', 'Month2NSFs', 'Month3NSFs', 'Month4NSFs',
      'Month1NegativeDays', 'Month2NegativeDays', 'Month3NegativeDays', 'Month4NegativeDays',
      'Month1DepositCount', 'Month2DepositCount', 'Month3DepositCount', 'Month4DepositCount'
    ];
    return wholeNumberFields.includes(fieldName);
  };

  // Helper function to convert decimal numbers to whole numbers
  const convertToWholeNumber = (value) => {
    if (value === null || value === undefined || value === '') {
      return value;
    }
    
    // Convert to string to handle both string and number inputs
    const stringValue = String(value);
    
    // If it contains a decimal point, truncate everything after it
    if (stringValue.includes('.')) {
      return stringValue.split('.')[0];
    }
    
    return stringValue;
  };

  const isTwoDecimalField = (fieldName) => {
    const twoDecimalFields = [
      'AverageMonthlySales',
      'AverageBankBalance',
      'AverageNSFs',
      'AverageNumberOfNegDays',
      'AverageNumberOfDeposits'
    ];

    return twoDecimalFields.includes(fieldName);
  };

  // Helper function to process business data and convert whole number fields
  const processBusinessDataForWholeNumbers = (data) => {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const processedData = { ...data };
    
    // Convert all whole number fields to remove decimals
    Object.keys(processedData).forEach(fieldName => {
      if (isWholeNumberField(fieldName)) {
        processedData[fieldName] = convertToWholeNumber(processedData[fieldName]);
      } else if (isTwoDecimalField(fieldName)) {
        processedData[fieldName] = formatToTwoDecimals(processedData[fieldName]);
      }
    });

    return processedData;
  };
  
  // Render field based on type
  const renderField = (field) => {
    // Use nullish coalescing (??) instead of || to preserve 0 values
    // This is important for NSFs and Negative Days which can legitimately be 0
    const value = businessData[field.name] ?? '';
    const hasError = validationErrors.some(error => error.field === field.name);
    
    // Determine if the field is required (all fields are required except for credit score)
    const isRequired = !field.name.toLowerCase().includes('creditscore');
    
    const isEmpty = value === undefined || value === null || 
      (typeof value !== 'number' && value.toString().trim() === '');
    
    // Determine if this is a dropdown field that needs special handling
    const isDropdown = field.name === 'EntityType' || 
                      field.name === 'BusinessState' || field.name === 'ContactState' || 
                      field.name === 'Contact2State';
    
    // Check if this is a phone number field
    const isPhoneField = field.name === 'ContactCellPhone' || field.name === 'Contact2CellPhone';
    
    // For dropdowns that are required, we want to always show validation errors if empty
    // For other fields, only show errors if showValidationErrors is true
    const isInvalid = hasError || (isEmpty && isRequired && showValidationErrors);
    
    // Debug logging for state dropdowns
    if (isDropdown && field.name.includes('State')) {
      console.log(`Rendering ${field.name}:`, {
        value,
        isEmpty,
        isRequired,
        hasError,
        showValidationErrors,
        isInvalid
      });
    }
    
    const validator = getValidatorForField(field);
    
    // Get validation error message directly from validationErrors state
    const errorFromState = validationErrors.find(error => error.field === field.name)?.message;
    
    // Run validation on current value (for street addresses and other validators)
    let validationResult = { isValid: true, message: '' };
    if (validator && validator.validate && value) {
      validationResult = validator.validate(value);
    }
    
    // Determine message priority:
    // 1. Error from validation state
    // 2. Live validation error
    // 3. Empty field error
    const displayMessage = errorFromState || 
                          (!validationResult.isValid ? validationResult.message : '') ||
                          (isEmpty && isRequired ? (isDropdown ? `Please select a ${field.label.toLowerCase()}` : `${field.label} is required`) : '');
    
    // For street address fields (BusinessStreet, ContactStreet, Contact2Street) use validation
    const isStreetField = field.name === 'BusinessStreet' || field.name === 'ContactStreet' || field.name === 'Contact2Street';
    
    // Log validation info for debugging 
    if (isStreetField) {
      console.log(`${field.name} validation:`, {
        value,
        hasError,
        validationResult,
        errorFromState,
        displayMessage
      });
    }
    
    // Render Industry field with dynamic picklist values from Salesforce
    if (field.name === 'Industry') {
      // Show loading state while fetching industries
      if (loadingIndustries) {
        return (
          <div className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" />
            <span className="text-muted">Loading industries...</span>
          </div>
        );
      }

      // Custom styles for the Select component to match Bootstrap
      const customSelectStyles = {
        control: (provided, state) => ({
          ...provided,
          minHeight: '32px',
          height: '38px',
          borderColor: isInvalid ? '#dc3545' : '#ced4da',
          boxShadow: 'none',
          '&:hover': {
            borderColor: isInvalid ? '#dc3545' : '#80bdff'
          },
          fontSize: '1rem'
        }),
        menu: (provided) => ({
          ...provided,
          zIndex: 9999
        }),
        menuPortal: (provided) => ({
          ...provided,
          zIndex: 9999
        }),
        option: (provided, state) => ({
          ...provided,
          backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : null,
          color: state.isSelected ? 'white' : 'inherit'
        })
      };
      
      return (
        <>
          <Select
            id={field.name}
            name={field.name}
            options={industryOptions}
            value={value ? { value, label: value } : null}
            onChange={(selectedOption) => handleInputChange(field.name, selectedOption ? selectedOption.value : '')}
            isSearchable={true}
            placeholder="Select or search for industry..."
            styles={customSelectStyles}
            className={isInvalid ? 'is-invalid' : ''}
            classNamePrefix="select"
            menuPortalTarget={document.body}
            onBlur={() => {
              if (isEmpty && isRequired) {
                setShowValidationErrors(true);
                validateForm(); // Force validation on blur
              }
            }}
          />
          {isInvalid && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {displayMessage || `${field.label} is required`}
            </div>
          )}
        </>
      );
    } 
    // Render EntityType field as a dropdown
    else if (field.name === 'EntityType') {
      return (
        <>
          <Form.Select
            id={field.name}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
            required={isRequired}
            className={`mt-0 ${isEmpty && isRequired ? 'is-invalid' : ''}`}
            onBlur={() => {
              validateForm(); // Force validation on blur
              setShowValidationErrors(true);
            }}
          >
            <option value="">Select Entity Type...</option>
            {entityTypes.map((entityType, index) => (
              <option key={index} value={entityType}>
                {entityType}
              </option>
            ))}
          </Form.Select>
          {(isInvalid || (isEmpty && isRequired)) && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {displayMessage || `Please select a ${field.label.toLowerCase()}`}
            </div>
          )}
        </>
      );
    }
    // Render State fields as dropdowns with strict validation
    else if (field.name === 'BusinessState' || field.name === 'ContactState' || field.name === 'Contact2State') {
      // Ensure the value is uppercase for consistent matching with dropdown options
      const uppercasedValue = typeof value === 'string' ? value.toUpperCase() : value;
      
      return (
        <>
          <Form.Select
            id={field.name}
            value={uppercasedValue}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
            required={isRequired}
            className={`mt-0 ${isEmpty && isRequired ? 'is-invalid' : ''}`}
            onBlur={() => {
              validateForm(); // Force validation on blur
              setShowValidationErrors(true);
            }}
          >
            <option value="">Select State...</option>
            {stateCodes.map((stateCode) => (
              <option key={stateCode} value={stateCode}>
                {stateCode}
              </option>
            ))}
          </Form.Select>
          {(isInvalid || (isEmpty && isRequired)) && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {displayMessage || `Please select a ${field.label.toLowerCase()}`}
            </div>
          )}
        </>
      );
    }
    // Render Country fields as dropdowns
    else if (field.name === 'BusinessCountry' || field.name === 'ContactCountry' || field.name === 'Contact2Country') {
      return (
        <>
          <Form.Select
            id={field.name}
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
            required={isRequired}
            className={`mt-0 ${isEmpty && isRequired ? 'is-invalid' : ''}`}
            onBlur={() => {
              validateForm();
              setShowValidationErrors(true);
            }}
          >
            <option value="">Select Country...</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </Form.Select>
          {(isInvalid || (isEmpty && isRequired)) && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {displayMessage || `Please select a ${field.label.toLowerCase()}`}
            </div>
          )}
        </>
      );
    }
    // Special handling for phone number fields
    else if (isPhoneField) {
      return (
        <>
          <Form.Control
            id={field.name}
            name={field.name}
            type="text"
            value={value}
            onChange={handlePhoneInputChange}
            isInvalid={isInvalid}
            required={isRequired}
            className="mt-0"
            onBlur={() => {
              if (isEmpty && isRequired) {
                setShowValidationErrors(true);
                validateForm(); // Force validation on blur
              }
            }}
          />
          {isInvalid && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {displayMessage}
            </div>
          )}
        </>
      );
    } else if (field.type === 'date' || field.name.toLowerCase().includes('dateofbirth') || field.name.toLowerCase().includes('dob')) {
      return (
        <>
          <Form.Control
            id={field.name}
            type="date"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
            required={isRequired}
            className="mt-0"
            onBlur={() => {
              // Validate date of birth on blur specifically
              if (field.name.toLowerCase().includes('dateofbirth') || field.name.toLowerCase().includes('dob')) {
                const validationResult = validateDateOfBirth.validate(value);
                if (!validationResult.isValid && value) {
                  // Only add error if there's a value (empty is handled separately)
                  setValidationErrors(prev => {
                    // Remove any existing error for this field
                    const filtered = prev.filter(err => err.field !== field.name);
                    // Add the new error
                    return [...filtered, {
                      field: field.name,
                      category: getMappedCategory(field.category),
                      message: `${field.label}: ${validationResult.message}`
                    }];
                  });
                }
              }
            }}
          />
          {isInvalid && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {displayMessage || `${field.label} is required`}
            </div>
          )}
        </>
      );
    } else if (validator) {
      // Special handling for validated fields like street addresses
      return (
        <ValidatedInput
          id={field.name}
          name={field.name}
          value={value}
          onChange={handleInputChange}
          validator={validator}
          required={isRequired}
          showValidation={true}
          validateOnChange={true}
          validateAfterLength={0}
          isInvalid={isInvalid}
          className="mt-0"
          errorMessage={displayMessage} // Pass the error message directly
        />
      );
    } else {
      // Regular text input for fields without a specific validator
      return (
        <>
          <Form.Control
            id={field.name}
            type={isNumericalField(field.name) ? "number" : "text"}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
            required={isRequired}
            className="mt-0"
            step={isWholeNumberField(field.name) ? "1" : "any"}
            min={isNumericalField(field.name) ? "0" : undefined}
            onBlur={() => {
              if (isEmpty && isRequired) {
                setShowValidationErrors(true);
                validateForm(); // Force validation on blur
              }
            }}
          />
          {isInvalid && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {displayMessage}
            </div>
          )}
        </>
      );
    }
  };
  
  // Render the active section
  const renderActiveSection = () => {
    // If we're on the File Uploads section
    if (activeSection === categories.length + 1) {
      return renderFileUploadsSection();
    }
    
    // If we're on the Lender Submission section
    if (activeSection === categories.length) {
      return renderLenderSubmissionSection();
    }
    
    const category = categories[activeSection];
    const categoryFields = businessFields.filter(field => 
      getMappedCategory(field.category) === category
    );
    
    // Filter out financial fields if we're in the Business Information category
    // since they'll be displayed in the Financial Information section
    // Also filter out CumulativeOwnershipPercentage from all categories since it's only used for lender matching
    const filteredCategoryFields = category === 'Business Information' ? 
      categoryFields.filter(field => 
        !['AnnualRevenue', 'AverageMonthlySales', 'RequestedAmount', 'AverageBankBalance',
          'AverageNSFs', 'AverageNumberOfNegDays', 'AverageNumberOfDeposits', 'CumulativeOwnershipPercentage'].includes(field.name)
      ) : 
      // Always filter out CumulativeOwnershipPercentage from all categories
      categoryFields.filter(field => field.name !== 'CumulativeOwnershipPercentage');
    
    // Group fields by their original category for better organization
    const fieldsByOriginalCategory = {};
    filteredCategoryFields.forEach(field => {
      if (!fieldsByOriginalCategory[field.category]) {
        fieldsByOriginalCategory[field.category] = [];
      }
      fieldsByOriginalCategory[field.category].push(field);
    });
    
    return (
      <div className="section-content">
        <div className="section-header d-flex justify-content-between align-items-center">
          <h4 className="section-title">{category}</h4>
          
          {/* Add Secondary Contact Button/Remove Button - Only show in Contact Information section */}
          {category === 'Contact Information' && (
            hasSecondaryContact ? (
              <Button 
                variant="outline-danger" 
                size="sm" 
                onClick={removeSecondaryContact}
                className="d-flex align-items-center"
              >
                <FaTimesCircle className="me-1" /> Remove Second Contact
              </Button>
            ) : (
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={addSecondaryContact}
                className="d-flex align-items-center"
              >
                <FaUserPlus className="me-1" /> Add Second Contact
              </Button>
            )
          )}
        </div>
        
        <div className="section-description">
          Please fill out all required fields in this section.
        </div>
        
        <Form>
          {/* Render fields grouped by their original category */}
          {Object.entries(fieldsByOriginalCategory).map(([originalCategory, fields], groupIndex) => (
            <div key={originalCategory}>
              {/* Only show subsection headers if this is a combined category */}
              {Object.keys(fieldsByOriginalCategory).length > 1 && (
                <h5 className="subsection-title mt-4 mb-3">
                  {originalCategory}
                </h5>
              )}
              
              <Row>
                {fields.map((field) => {
                  // Check if this field is optional (credit score or optional bank statement fields)
                  // Month 1-3 Deposits and DepositCount are required, Month 4 and all NSFs/NegativeDays are optional
                  const isOptionalField = field.name.toLowerCase().includes('creditscore') || isOptionalBankStatementField(field.name);
                  
                  return (
                    <Col md={6} key={field.name} className="mb-3">
                      <Form.Group className="mb-0">
                        <Form.Label className="form-label" htmlFor={field.name}>
                          {field.label}
                          {/* Show required indicator only for required fields */}
                          {!isOptionalField ? (
                            <span className="required">*</span>
                          ) : (
                            <span className="text-muted"> (optional)</span>
                          )}
                        </Form.Label>
                        {renderField(field)}
                      </Form.Group>
                    </Col>
                  );
                })}
              </Row>
            </div>
          ))}
          
          {/* Additional Details Section - Added to Business Information tab */}
          {category === 'Business Information' && (
            <div className="additional-details-section mt-4 pt-3 border-top">
              <h5 className="subsection-title mb-3">Financial Information</h5>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Annual Revenue <span className="required">*</span></Form.Label>
                    <Form.Control
                      id="AnnualRevenue"
                      type="number"
                      min="0"
                      step="0.01"
                      value={businessData.AnnualRevenue || ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '') {
                          handleInputChange('AnnualRevenue', '');
                          return;
                        }
                        const value = parseFloat(inputValue);
                        if (isNaN(value) || value < 0) {
                          return;
                        }
                        handleInputChange('AnnualRevenue', value);
                      }}
                      onBlur={() => {
                        const value = businessData.AnnualRevenue;
                        const isEmpty = value === undefined || value === null || value === '' || 
                          (typeof value === 'string' && value.trim() === '') ||
                          (typeof value === 'number' && isNaN(value));
                        
                        if (isEmpty) {
                          setValidationErrors(prev => {
                            const filtered = prev.filter(err => err.field !== 'AnnualRevenue');
                            return [...filtered, {
                              field: 'AnnualRevenue',
                              category: 'Business Information',
                              message: 'Annual Revenue is required'
                            }];
                          });
                        } else {
                          setValidationErrors(prev => prev.filter(err => err.field !== 'AnnualRevenue'));
                        }
                      }}
                      placeholder="Enter annual revenue"
                      isInvalid={validationErrors.some(error => error.field === 'AnnualRevenue')}
                      required={true}
                    />
                    {validationErrors.some(error => error.field === 'AnnualRevenue') && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        {validationErrors.find(error => error.field === 'AnnualRevenue')?.message || 'Annual Revenue is required'}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Average Monthly Sales <span className="required">*</span></Form.Label>
                    <Form.Control
                      id="AverageMonthlySales"
                      type="number"
                      min="0"
                      step="0.01"
                      value={businessData.AverageMonthlySales || ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '') {
                          handleInputChange('AverageMonthlySales', '');
                          return;
                        }
                        const value = parseFloat(inputValue);
                        if (isNaN(value) || value < 0) {
                          return;
                        }
                        handleInputChange('AverageMonthlySales', value);
                      }}
                      onBlur={() => {
                        const value = businessData.AverageMonthlySales;
                        const isEmpty = value === undefined || value === null || value === '' || 
                          (typeof value === 'string' && value.trim() === '') ||
                          (typeof value === 'number' && isNaN(value));
                        
                        if (isEmpty) {
                          setValidationErrors(prev => {
                            const filtered = prev.filter(err => err.field !== 'AverageMonthlySales');
                            return [...filtered, {
                              field: 'AverageMonthlySales',
                              category: 'Business Information',
                              message: 'Average Monthly Sales is required'
                            }];
                          });
                        } else {
                          setValidationErrors(prev => prev.filter(err => err.field !== 'AverageMonthlySales'));
                        }
                      }}
                      placeholder="Enter average monthly sales"
                      isInvalid={validationErrors.some(error => error.field === 'AverageMonthlySales')}
                      required={true}
                    />
                    {validationErrors.some(error => error.field === 'AverageMonthlySales') && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        {validationErrors.find(error => error.field === 'AverageMonthlySales')?.message || 'Average Monthly Sales is required'}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Requested Amount <span className="required">*</span></Form.Label>
                    <Form.Control
                      id="RequestedAmount"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={businessData.RequestedAmount ?? ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '') {
                          handleInputChange('RequestedAmount', '');
                          return;
                        }
                        const value = parseFloat(inputValue);
                        if (isNaN(value) || value < 0) {
                          return;
                        }
                        handleInputChange('RequestedAmount', value);
                      }}
                      onBlur={() => {
                        const value = businessData.RequestedAmount;
                        if (!isValidRequestedAmount(value)) {
                          setValidationErrors(prev => {
                            const filtered = prev.filter(err => err.field !== 'RequestedAmount');
                            return [...filtered, {
                              field: 'RequestedAmount',
                              category: 'Business Information',
                              message: 'Requested Amount must be a valid number greater than zero'
                            }];
                          });
                        } else {
                          setValidationErrors(prev => prev.filter(err => err.field !== 'RequestedAmount'));
                        }
                      }}
                      placeholder="Enter requested amount"
                      isInvalid={validationErrors.some(error => error.field === 'RequestedAmount')}
                      required={true}
                    />
                    {validationErrors.some(error => error.field === 'RequestedAmount') && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        {validationErrors.find(error => error.field === 'RequestedAmount')?.message || 'Requested Amount is required'}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Average Bank Balance <span className="required">*</span></Form.Label>
                    <Form.Control
                      id="AverageBankBalance"
                      type="number"
                      min="0"
                      step="0.01"
                      value={businessData.AverageBankBalance || ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '') {
                          handleInputChange('AverageBankBalance', '');
                          return;
                        }
                        const value = parseFloat(inputValue);
                        if (isNaN(value) || value < 0) {
                          return;
                        }
                        handleInputChange('AverageBankBalance', value);
                      }}
                      onBlur={() => {
                        const value = businessData.AverageBankBalance;
                        const isEmpty = value === undefined || value === null || value === '' || 
                          (typeof value === 'string' && value.trim() === '') ||
                          (typeof value === 'number' && isNaN(value));
                        
                        if (isEmpty) {
                          setValidationErrors(prev => {
                            const filtered = prev.filter(err => err.field !== 'AverageBankBalance');
                            return [...filtered, {
                              field: 'AverageBankBalance',
                              category: 'Business Information',
                              message: 'Average Bank Balance is required'
                            }];
                          });
                        } else {
                          setValidationErrors(prev => prev.filter(err => err.field !== 'AverageBankBalance'));
                        }
                      }}
                      placeholder="Enter average bank balance"
                      isInvalid={validationErrors.some(error => error.field === 'AverageBankBalance')}
                      required={true}
                    />
                    {validationErrors.some(error => error.field === 'AverageBankBalance') && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        {validationErrors.find(error => error.field === 'AverageBankBalance')?.message || 'Average Bank Balance is required'}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Number of Positions <span className="text-muted"> (optional)</span></Form.Label>
                    <Form.Control
                      id="NumberOfPositions"
                      type="number"
                      min="0"
                      max="6"
                      value={businessData.NumberOfPositions || ''}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (isNaN(value) || value < 0 || value > 6) {
                          return;
                        }
                        handleInputChange('NumberOfPositions', value);
                      }}
                      placeholder="Enter a number between 0-6"
                    />
                    <Form.Text className="text-muted">
                      Enter a whole number between 0 and 6
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Payoff Amount Needed <span className="text-muted"> (optional)</span></Form.Label>
                    <Form.Control
                      id="PayoffAmountNeeded"
                      type="number"
                      min="0"
                      step="0.01"
                      value={businessData.PayoffAmountNeeded || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (isNaN(value) || value < 0) {
                          return;
                        }
                        handleInputChange('PayoffAmountNeeded', value);
                      }}
                      placeholder="Enter amount"
                    />
                    <Form.Text className="text-muted">
                      Enter the payoff amount needed (if applicable)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Average NSFs <span className="text-muted"> (optional)</span></Form.Label>
                    <Form.Control
                      id="AverageNSFs"
                      type="number"
                      min="0"
                      step="0.01"
                      value={businessData.AverageNSFs ?? ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '') {
                          handleInputChange('AverageNSFs', '');
                          return;
                        }
                        const value = parseFloat(inputValue);
                        // Allow 0 and positive decimal values
                        if (isNaN(value) || value < 0) {
                          return;
                        }
                        handleInputChange('AverageNSFs', value);
                      }}
                      placeholder="Enter average NSFs"
                      isInvalid={validationErrors.some(error => error.field === 'AverageNSFs')}
                      required={false}
                    />
                    {validationErrors.some(error => error.field === 'AverageNSFs') && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        {validationErrors.find(error => error.field === 'AverageNSFs')?.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Average Number of Neg Days <span className="text-muted"> (optional)</span></Form.Label>
                    <Form.Control
                      id="AverageNumberOfNegDays"
                      type="number"
                      min="0"
                      step="0.01"
                      value={businessData.AverageNumberOfNegDays ?? ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '') {
                          handleInputChange('AverageNumberOfNegDays', '');
                          return;
                        }
                        const value = parseFloat(inputValue);
                        // Allow 0 and positive decimal values
                        if (isNaN(value) || value < 0) {
                          return;
                        }
                        handleInputChange('AverageNumberOfNegDays', value);
                      }}
                      placeholder="Enter average neg days"
                      isInvalid={validationErrors.some(error => error.field === 'AverageNumberOfNegDays')}
                      required={false}
                    />
                    {validationErrors.some(error => error.field === 'AverageNumberOfNegDays') && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        {validationErrors.find(error => error.field === 'AverageNumberOfNegDays')?.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Average Number of Deposits <span className="text-muted"> (optional)</span></Form.Label>
                    <Form.Control
                      id="AverageNumberOfDeposits"
                      type="number"
                      min="0"
                      step="0.01"
                      value={businessData.AverageNumberOfDeposits ?? ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (inputValue === '') {
                          handleInputChange('AverageNumberOfDeposits', '');
                          return;
                        }
                        const value = parseFloat(inputValue);
                        // Allow 0 and positive decimal values
                        if (isNaN(value) || value < 0) {
                          return;
                        }
                        handleInputChange('AverageNumberOfDeposits', value);
                      }}
                      placeholder="Enter average number of deposits"
                      isInvalid={validationErrors.some(error => error.field === 'AverageNumberOfDeposits')}
                      required={false}
                    />
                    {validationErrors.some(error => error.field === 'AverageNumberOfDeposits') && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        {validationErrors.find(error => error.field === 'AverageNumberOfDeposits')?.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}
          
          {/* Secondary Contact Section - Only show in Contact Information section and when hasSecondaryContact is true */}
          {category === 'Contact Information' && hasSecondaryContact && (
            <div className="secondary-contact-section">
              <h5 className="subsection-title mt-4 mb-3 pt-2 border-top">
                Secondary Contact Information
              </h5>
              
              {/* Dynamically render secondary contact fields from mappings */}
              {secondaryFieldsLoading ? (
                <div className="text-center my-4">
                  <Spinner animation="border" size="sm" /> Loading secondary contact fields...
                </div>
              ) : (
                <>
                  {/* Personal Information Fields */}
                  <Row>
                    {secondaryContactFields
                      .filter(field => 
                        !field.opportunityField.includes('Street') && 
                        !field.opportunityField.includes('City') && 
                        !field.opportunityField.includes('State') && 
                        !field.opportunityField.includes('ZipCode')
                      )
                      .map(field => (
                        <Col md={6} key={field.opportunityField} className="mb-3">
                          <Form.Group className="mb-0">
                            <Form.Label className="form-label" htmlFor={field.opportunityField}>
                              {field.label}
                              {/* Make Credit Score optional for secondary contact */}
                              {!field.opportunityField.toLowerCase().includes('creditscore') ? (
                                <span className="required">*</span>
                              ) : (
                                <span className="text-muted"> (optional)</span>
                              )}
                            </Form.Label>
                            {(() => {
                              // Determine field type and validator based on field name
                              let fieldType = 'text';
                              let validator = null;
                              
                              if (field.opportunityField === 'Contact2DateOfBirth') {
                                fieldType = 'date';
                                validator = validateDateOfBirth;
                              } else if (field.opportunityField === 'Contact2SSN') {
                                validator = validateSSN;
                              } else if (field.opportunityField === 'Contact2Email') {
                                validator = validateEmail;
                              } else if (field.opportunityField === 'Contact2CellPhone') {
                                validator = validatePhone;
                              } else if (field.opportunityField === 'Contact2ZipCode') {
                                validator = validateZip;
                              } else if (field.opportunityField === 'Contact2OwnershipPercentage') {
                                validator = validatePercentage;
                              } else if (field.opportunityField === 'Contact2CreditScore') {
                                validator = validateCreditScore;
                              }

                              // Check if field is required
                              const isRequired = !field.opportunityField.toLowerCase().includes('creditscore');
                              
                              // Render with appropriate validator
                              if (field.opportunityField === 'Contact2DateOfBirth') {
                                return (
                                  <>
                                    <Form.Control
                                      id={field.opportunityField}
                                      type="date"
                                      value={businessData[field.opportunityField] || ''}
                                      onChange={(e) => handleInputChange(field.opportunityField, e.target.value)}
                                      isInvalid={validationErrors.some(error => error.field === field.opportunityField)}
                                      required={isRequired}
                                      className="mt-0"
                                      onBlur={() => {
                                        // Validate date of birth on blur specifically
                                        const validationResult = validateDateOfBirth.validate(businessData[field.opportunityField]);
                                        if (!validationResult.isValid && businessData[field.opportunityField]) {
                                          // Only add error if there's a value (empty is handled separately)
                                          setValidationErrors(prev => {
                                            // Remove any existing error for this field
                                            const filtered = prev.filter(err => err.field !== field.opportunityField);
                                            // Add the new error
                                            return [...filtered, {
                                              field: field.opportunityField,
                                              category: 'Contact Information',
                                              message: `${field.label}: ${validationResult.message}`
                                            }];
                                          });
                                        }
                                      }}
                                    />
                                    {validationErrors.some(error => error.field === field.opportunityField) && (
                                      <div className="invalid-feedback" style={{ display: 'block' }}>
                                        {validationErrors.find(error => error.field === field.opportunityField)?.message || `${field.label} is required`}
                                      </div>
                                    )}
                                  </>
                                );
                              } else if (validator) {
                                return (
                                  <ValidatedInput
                                    id={field.opportunityField}
                                    name={field.opportunityField}
                                    value={businessData[field.opportunityField] || ''}
                                    onChange={handleInputChange}
                                    validator={validator}
                                    required={isRequired}
                                    showValidation={true}
                                    validateOnChange={true}
                                    validateAfterLength={0}
                                    isInvalid={validationErrors.some(error => error.field === field.opportunityField)}
                                    className="mt-0"
                                  />
                                );
                              } else {
                                return (
                                  <>
                                    <Form.Control
                                      id={field.opportunityField}
                                      type={fieldType}
                                      value={businessData[field.opportunityField] || ''}
                                      onChange={(e) => handleInputChange(field.opportunityField, e.target.value)}
                                      isInvalid={validationErrors.some(error => error.field === field.opportunityField)}
                                      required={isRequired}
                                      className="mt-0"
                                    />
                                    {validationErrors.some(error => error.field === field.opportunityField) && (
                                      <div className="invalid-feedback" style={{ display: 'block' }}>
                                        {validationErrors.find(error => error.field === field.opportunityField)?.message || `${field.label} is required`}
                                      </div>
                                    )}
                                  </>
                                );
                              }
                            })()}
                          </Form.Group>
                        </Col>
                      ))}
                  </Row>
                  
                  {/* Secondary Contact Address - reduced top margin */}
                  {secondaryContactFields.some(field => 
                    field.opportunityField.includes('Street') || 
                    field.opportunityField.includes('City') || 
                    field.opportunityField.includes('State') || 
                    field.opportunityField.includes('ZipCode')
                  ) && (
                    <>
                      <h5 className="subsection-title mt-2 mb-3">
                        Secondary Contact Address
                      </h5>
                      
                      <Row>
                        {secondaryContactFields
                          .filter(field => 
                            field.opportunityField.includes('Street') || 
                            field.opportunityField.includes('City') || 
                            field.opportunityField.includes('State') || 
                            field.opportunityField.includes('ZipCode')
                          )
                          .map(field => (
                            <Col md={6} key={field.opportunityField} className="mb-3">
                              <Form.Group className="mb-0">
                                <Form.Label className="form-label" htmlFor={field.opportunityField}>
                                  {field.label}
                                  {/* Make Credit Score optional for secondary contact */}
                                  {!field.opportunityField.toLowerCase().includes('creditscore') ? (
                                    <span className="required">*</span>
                                  ) : (
                                    <span className="text-muted"> (optional)</span>
                                  )}
                                </Form.Label>
                                {field.opportunityField === 'Contact2ZipCode' ? (
                                  <ValidatedInput
                                    id={field.opportunityField}
                                    name={field.opportunityField}
                                    value={businessData[field.opportunityField] || ''}
                                    onChange={handleInputChange}
                                    validator={validateZip}
                                    required={!field.opportunityField.toLowerCase().includes('creditscore')}
                                    showValidation={true}
                                    validateOnChange={true}
                                    validateAfterLength={0}
                                    isInvalid={validationErrors.some(error => error.field === field.opportunityField)}
                                    className="mt-0"
                                  />
                                ) : field.opportunityField === 'Contact2State' ? (
                                  <>
                                    <Form.Select
                                      id={field.opportunityField}
                                      value={businessData[field.opportunityField] || ''}
                                      onChange={(e) => handleInputChange(field.opportunityField, e.target.value)}
                                      isInvalid={validationErrors.some(error => error.field === field.opportunityField)}
                                      required={!field.opportunityField.toLowerCase().includes('creditscore')}
                                      className="mt-0"
                                    >
                                      <option value="">Select State...</option>
                                      {stateCodes.map((stateCode) => (
                                        <option key={stateCode} value={stateCode}>
                                          {stateCode}
                                        </option>
                                      ))}
                                    </Form.Select>
                                    {validationErrors.some(error => error.field === field.opportunityField) && (
                                      <div className="invalid-feedback" style={{ display: 'block' }}>
                                        {validationErrors.find(error => error.field === field.opportunityField)?.message || `${field.label} is required`}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Form.Control
                                      id={field.opportunityField}
                                      type="text"
                                      value={businessData[field.opportunityField] || ''}
                                      onChange={(e) => handleInputChange(field.opportunityField, e.target.value)}
                                      isInvalid={validationErrors.some(error => error.field === field.opportunityField)}
                                      required={!field.opportunityField.toLowerCase().includes('creditscore')}
                                      className="mt-0"
                                    />
                                    {validationErrors.some(error => error.field === field.opportunityField) && (
                                      <div className="invalid-feedback" style={{ display: 'block' }}>
                                        {validationErrors.find(error => error.field === field.opportunityField)?.message || `${field.label} is required`}
                                      </div>
                                    )}
                                  </>
                                )}
                              </Form.Group>
                            </Col>
                          ))}
                      </Row>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          
          <div className="navigation-buttons">
            <div>
              {/* Navigation flow: Business (1) → Contact (0) → Files (4) → Submit (3) → Statements (2) */}
              {/* Previous button logic based on current section */}
              {(() => {
                const statementsIndex = categories.indexOf('Bank Statement Data');
                const businessIndex = categories.indexOf('Business Information');
                const contactIndex = categories.indexOf('Contact Information');
                
                // Business Information - no previous (it's the start)
                if (activeSection === businessIndex) {
                  return null;
                }
                // Contact Information - go back to Business
                if (activeSection === contactIndex) {
                  return (
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setActiveSection(businessIndex)}
                      size="md"
                      className="px-3"
                    >
                      Previous
                    </Button>
                  );
                }
                // Bank Statement Data (Statements) - go back to Submit
                if (activeSection === statementsIndex) {
                  return (
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => setActiveSection(categories.length)}
                      size="md"
                      className="px-3"
                    >
                      Previous
                    </Button>
                  );
                }
                // Default fallback for other sections
                return (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setActiveSection(activeSection - 1)}
                    size="md"
                    className="px-3"
                  >
                    Previous
                  </Button>
                );
              })()}
            </div>
            
            <div>
              {/* Next button logic based on current section */}
              {(() => {
                const statementsIndex = categories.indexOf('Bank Statement Data');
                const businessIndex = categories.indexOf('Business Information');
                const contactIndex = categories.indexOf('Contact Information');
                
                // Business Information - go to Contact
                if (activeSection === businessIndex) {
                  return (
                    <Button 
                      variant="primary" 
                      onClick={() => setActiveSection(contactIndex)}
                      size="md"
                      className="px-4 fw-bold"
                    >
                      Next
                    </Button>
                  );
                }
                // Contact Information - go to Files
                if (activeSection === contactIndex) {
                  return (
                    <Button 
                      variant="primary" 
                      onClick={() => setActiveSection(categories.length + 1)}
                      size="md"
                      className="px-4 fw-bold"
                    >
                      Next
                    </Button>
                  );
                }
                // Bank Statement Data (Statements) - no next (it's the end)
                if (activeSection === statementsIndex) {
                  return null;
                }
                // Default fallback
                return (
                  <Button 
                    variant="primary" 
                    onClick={() => setActiveSection(activeSection + 1)}
                    size="md"
                    className="px-4 fw-bold"
                  >
                    Next
                  </Button>
                );
              })()}
            </div>
          </div>
        </Form>
      </div>
    );
  };
  
  // Render the file uploads section
  const renderFileUploadsSection = () => {
    return (
      <div className="section-content">
        <FileUploads
          bankFiles={bankFiles}
          setBankFiles={setBankFiles}
          appFiles={appFiles}
          setAppFiles={setAppFiles}
          quoteFiles={quoteFiles}
          setQuoteFiles={setQuoteFiles}
          creditFiles={creditFiles}
          setCreditFiles={setCreditFiles}
          assetFormFiles={assetFormFiles}
          setAssetFormFiles={setAssetFormFiles}
          disabled={syncLoading}
          submitting={syncLoading}
          salesforceFiles={salesforceFiles}
          loadingSfFiles={loadingSfFiles}
          sfFilesError={sfFilesError}
          financingType={activeFinancingTab}
        />
        
        <div className="navigation-buttons">
          <div>
            <Button 
              variant="outline-secondary" 
              onClick={() => setActiveSection(0)} // Go back to Contact Information
              size="md"
              className="px-3"
            >
              Previous
            </Button>
          </div>
          
          <div>
            <Button 
              variant="primary" 
              onClick={() => setActiveSection(categories.length)} // Go to Submit (Lender Submission)
              size="md"
              className="px-4 fw-bold"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  };
  
  // Modify the lender submission section
  const renderLenderSubmissionSection = () => {
    const hasErrors = validationErrors.length > 0;
    const filesRequired = !fileUploadsComplete;
    
    // Determine title based on financing type
    const sectionTitle = activeFinancingTab === 'equipment-financing' 
      ? 'Submit to Equipment Financing Lender' 
      : activeFinancingTab === 'sba'
      ? 'Submit to SBA Lender'
      : activeFinancingTab === 'real-estate'
      ? 'Submit to Real Estate Lender'
      : 'Submit to Working Capital Lender';
    
    return (
      <div className="section-content">
        <div className="section-header">
          <h4 className="section-title">{sectionTitle}</h4>
        </div>
        
        {/* Lender Submission Component */}
        <div className="lender-submission-wrapper">
          {hasErrors && (
            <Alert variant="danger" className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="me-2">⚠️</span>
                  <span>Please fix all validation errors before submitting to lenders.</span>
                </div>
                <Button variant="outline-danger" size="sm" onClick={goToFirstError}>
                  Go to first error
                </Button>
              </div>
            </Alert>
          )}
          
          {filesRequired && (
            <Alert variant="warning" className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="me-2">⚠️</span>
                  {activeFinancingTab === 'equipment-financing' ? (
                    <span>Please upload application documents, quotes, and credit information.</span>
                  ) : activeFinancingTab === 'real-estate' ? (
                    <span>Please upload application documents and asset form.</span>
                  ) : (
                    <span>Please upload at least one bank statement and one application document.</span>
                  )}
                </div>
                <Button variant="outline-warning" size="sm" onClick={() => setActiveSection(categories.length + 1)}>
                  Go to File Uploads
                </Button>
              </div>
            </Alert>
          )}

          <LenderSubmission 
            contactData={contactData} 
            opportunityData={businessData}
            bankFiles={bankFiles}
            appFiles={appFiles}
            quoteFiles={quoteFiles}
            creditFiles={creditFiles}
            assetFormFiles={assetFormFiles}
            disabled={hasErrors || filesRequired || syncLoading}
            financingType={activeFinancingTab}
            preloadedLenders={
              activeFinancingTab === 'working-capital' ? workingCapitalLenders :
              activeFinancingTab === 'equipment-financing' ? equipmentFinancingLenders :
              activeFinancingTab === 'real-estate' ? realEstateLenders :
              sbaLenders
            }
            lendersLoading={lendersLoading}
            preloadedAdvancedCriteria={advancedCriteriaSettings}
            advancedCriteriaLoading={advancedCriteriaLoading}
          />
          
          {/* Sync link at the bottom of the lender section */}
          <div className="mt-3 text-start">
            <Button
              size="sm"
              variant={!hasErrors ? (syncStatus?.type === 'success' ? "outline-success" : "outline-primary") : "outline-secondary"}
              className="compact-sync-btn"
              onClick={!hasErrors && !syncLoading ? handleSyncWithCRM : undefined}
              disabled={syncLoading || hasErrors}
            >
              {syncLoading ? (
                <div className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-1" />
                  <span>Syncing...</span>
                </div>
              ) : (
                <>
                  {!hasErrors && syncStatus?.type === 'success' ? "✓ " : ""}
                  Sync with Salesforce
                </>
              )}
            </Button>
            
            {hasErrors && (
              <div className="text-danger mt-1 small">
                <em>
                  Fix all validation errors to enable sync
                </em>
              </div>
            )}
            
            {syncStatus && (
              <div className={`text-${syncStatus.type === 'error' ? 'danger' : syncStatus.type} mt-1 small`}>
                {syncStatus.message}
              </div>
            )}
          </div>
        </div>
        
        <div className="navigation-buttons">
          <div>
            <Button 
              variant="outline-secondary" 
              onClick={() => setActiveSection(categories.length + 1)} // Go back to Files
              size="md"
              className="px-3"
            >
              Previous
            </Button>
          </div>
          
          <div>
            {/* Next goes to Bank Statement Data (Statements) */}
            <Button 
              variant="primary" 
              onClick={() => setActiveSection(categories.indexOf('Bank Statement Data') !== -1 ? categories.indexOf('Bank Statement Data') : categories.length - 1)}
              size="md"
              className="px-4 fw-bold"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  };
  
  // Calculate errors by category for navigation display
  const getErrorCountByCategory = () => {
    const errorCounts = {};
    
    validationErrors.forEach(error => {
      if (!errorCounts[error.category]) {
        errorCounts[error.category] = 0;
      }
      errorCounts[error.category]++;
    });
    
    return errorCounts;
  };
  
  // Handle scrolling to the first error field
  const scrollToField = (fieldId) => {
    const element = document.getElementById(fieldId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
      // Highlight the field
      element.classList.add('highlight-error');
      setTimeout(() => {
        element.classList.remove('highlight-error');
      }, 2000);
    }
  };
  
  // Go to the first error - navigate to the correct tab and scroll to the field
  const goToFirstError = () => {
    if (validationErrors.length > 0) {
      const firstError = validationErrors[0];
      
      // Find the category of the first error
      const errorCategory = firstError.category;
      
      // Find which section this category belongs to
      const categoryIndex = categories.findIndex(category => category === errorCategory);
      
      // Special handling for File Upload errors
      if (errorCategory === 'File Uploads' || firstError.field.includes('file')) {
        // Navigate to the File Uploads section
        setActiveSection(categories.length + 1);
      } 
      // If it's a valid category index, navigate to that section
      else if (categoryIndex !== -1) {
        setActiveSection(categoryIndex);
        
        // Give a moment for the section to render before scrolling
        setTimeout(() => {
          scrollToField(firstError.field);
        }, 100);
      } 
      // Default fallback - just try to scroll to the field without changing tabs
      else {
        scrollToField(firstError.field);
      }
    }
  };
  
  // Function to format contactData for SideNavigation to prevent errors
  const formatContactDataForNav = (contactData) => {
    if (!contactData) return null;
    
    // Ensure contact has required properties
    return {
      FirstName: contactData.FirstName || '',
      LastName: contactData.LastName || '',
      Email: contactData.Email || '',
      Id: contactData.Id || 'N/A'
    };
  };
  
  // Modify the useEffect for contact data to auto-populate title if empty
  useEffect(() => {
    if (!loading && Object.keys(businessData).length > 0) {
      // Check if Title is empty and auto-populate with "Owner"
      if (!businessData.ContactTitle || businessData.ContactTitle.trim() === '') {
        setBusinessData(prev => ({
          ...prev,
          ContactTitle: "Owner"
        }));
      }
      
      // Check if secondary contact has a title field and auto-populate if empty
      if (hasSecondaryContact && (!businessData.Contact2Title || businessData.Contact2Title.trim() === '')) {
        setBusinessData(prev => ({
          ...prev,
          Contact2Title: "Owner"
        }));
      }
      
      // Format phone numbers if they exist
      if (businessData.ContactCellPhone) {
        const formattedPhone = validatePhone.format(businessData.ContactCellPhone);
        if (formattedPhone !== businessData.ContactCellPhone) {
          setBusinessData(prev => ({
            ...prev,
            ContactCellPhone: formattedPhone
          }));
        }
      }
      
      if (hasSecondaryContact && businessData.Contact2CellPhone) {
        const formattedPhone = validatePhone.format(businessData.Contact2CellPhone);
        if (formattedPhone !== businessData.Contact2CellPhone) {
          setBusinessData(prev => ({
            ...prev,
            Contact2CellPhone: formattedPhone
          }));
        }
      }
    }
  }, [loading, businessData, hasSecondaryContact]);
  
  // Loading spinner
  if (loading) {
    return (
      <div className="opportunity-detail">
        <div className="opportunity-detail-container" style={{ opacity: 0 }}>
          <Container fluid>
            <Row className="g-0">
              {/* Placeholder for sidebar */}
              <Col lg={3} xl={2} className="sidebar">
                <div style={{ minHeight: '100vh' }}></div>
              </Col>
              
              {/* Placeholder for main content */}
              <Col lg={9} xl={10} className="main-content">
                <div style={{ minHeight: '100vh' }}></div>
              </Col>
            </Row>
          </Container>
        </div>
        
        {/* Dots loading indicator */}
        <div className="dots-loading-container">
          <div className="dots-loading">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error message
  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Container>
    );
  }

  // Main content
  return (
    <div className="opportunity-detail">
      <div className="opportunity-detail-container fade-in">
        <Container fluid>
          <Row className="g-0">
            {/* Left Navigation */}
            <Col lg={3} xl={2} className="sidebar">
              <SideNavigation 
                categories={categories}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                completionStats={completionStats}
                contactData={formatContactDataForNav(contactData)}
                errorCounts={getErrorCountByCategory()}
                fileUploadStats={fileUploadStats}
                fileUploadsComplete={fileUploadsComplete}
              />
            </Col>
            
            {/* Main Content */}
            <Col lg={9} xl={10} className="main-content">
              {/* Financing Type Tabs */}
              <div className="financing-tabs-container mb-0">
                <Nav 
                  variant="tabs" 
                  activeKey={activeFinancingTab}
                  onSelect={(k) => setActiveFinancingTab(k)}
                  className="financing-tabs"
                >
                  <Nav.Item>
                    <Nav.Link eventKey="working-capital">
                      Working Capital
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="sba">
                      SBA
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="real-estate">
                      Real Estate
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="equipment-financing">
                      Equipment Financing
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>
              
              {/* Sync Status */}
              {syncStatus && (
                <Alert variant={syncStatus.type === 'error' ? 'danger' : syncStatus.type} className="mb-3 mt-3 mx-3">
                  {syncStatus.message}
                </Alert>
              )}
              
              {/* Active Section Content */}
              <div className="active-section-wrapper active-tab-content">
                {renderActiveSection()}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* OptOut Modal - blocks UI until user opens the link */}
      <Modal 
        show={showOptOutModal} 
        onHide={() => {}} 
        backdrop="static" 
        keyboard={false}
        centered
        size="lg"
      >
        <Modal.Header style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          border: 'none'
        }}>
          <Modal.Title>
            <strong>⚠️ Required: Credit Bureau Opt-Out</strong>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px' }}>
          <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
            <p style={{ marginBottom: '20px' }}>
              <strong>Before proceeding with lender submissions, you must complete the credit bureau opt-out process.</strong>
            </p>
            <p style={{ marginBottom: '20px' }}>
              This is a <strong>one-time requirement</strong> that helps protect your client's credit privacy during the funding process.
            </p>
            <div style={{ 
              background: '#f8f9fa', 
              padding: '20px', 
              borderRadius: '8px',
              marginBottom: '20px',
              border: '2px solid #667eea'
            }}>
              <h5 style={{ color: '#667eea', marginBottom: '15px' }}>What to do:</h5>
              <ol style={{ marginBottom: '0', paddingLeft: '20px' }}>
                <li style={{ marginBottom: '10px' }}>Click the button below to open the OptOut form in a new tab</li>
                <li style={{ marginBottom: '10px' }}>The form will be <strong>automatically filled</strong> with your client's information</li>
                <li style={{ marginBottom: '10px' }}>Verify the information is correct</li>
                <li style={{ marginBottom: '10px' }}>Complete the CAPTCHA</li>
                <li style={{ marginBottom: '10px' }}>Submit the form</li>
                <li>Return here and click "I've Completed the Opt-Out"</li>
              </ol>
            </div>
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  window.open(optOutUrl, '_blank');
                  success(
                    'Form opened! Complete the opt-out process and return here.',
                    'Credit Opt-Out',
                    8000
                  );
                }}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: '15px 40px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  marginBottom: '15px'
                }}
              >
                🔗 Open OptOut Form (Auto-Fill Enabled)
              </Button>
              <br />
              <Button
                variant="success"
                size="lg"
                onClick={async () => {
                  try {
                    // Mark as completed in the database
                    await axios.post('/api/prefill/mark-completed', {
                      opportunityId
                    });
                    
                    setShowOptOutModal(false);
                    success(
                      'Thank you! You can now proceed with lender submissions.',
                      'Opt-Out Complete',
                      5000
                    );
                  } catch (error) {
                    console.error('Error marking OptOut as completed:', error);
                    // Still close the modal even if the API call fails
                    setShowOptOutModal(false);
                    success(
                      'Modal closed. You can now proceed.',
                      'Continue',
                      3000
                    );
                  }
                }}
                style={{
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                ✅ I've Completed the Opt-Out
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OpportunityDetail;
